import { Component, OnInit, computed, inject, input, signal, effect } from '@angular/core';
import { DecimalPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { PermissionsService } from '../../core/services/permissions.service';
import { OrderReceiptView } from '../../components/order-receipt';
import {
    LocationOrderDetail,
    LocationOrderListItem,
    OrderDispatches,
    OrderDeliveryDetail,
    OrderPrintState,
    ReplayWebhooksResult,
    ReprintOrderResult,
    OrderLocationStats,
    OrderStatusHistory,
    OrderStreamEvent,
    OrderTimelineItem, OrderPaymentSummary } from '../../core/models';
import { orderEventAliasLabel, orderLabel, orderTokenOptions, orderTooltip } from '../../core/order-vocabulary';
import { OrderFeeLine, OrderLineState, OrderState } from '../../core/models';

// Sources whose orders are sales a store took at its own counter (OrderSources.StartedAtPointOfSale on the server).
// The platform only records them: they are never printed here, sent to a POS, or sent back to the store.
const POINT_OF_SALE_SOURCES = ['edge'];

/** One row of the Refunds table: a line a refund gave back, or the refund's total closing its group. The
 *  refund's own columns (date, reason, status) are shown on its first row only. */
interface RefundRow {
    first: boolean;
    refundedAt: string | null;
    reason: string;
    status: string;
    item: string;
    qty: number | null;
    amount: number;
    kind: 'product' | 'fee' | 'total';
    isTotal: boolean;
    /** The refund's lines add up to something other than what it returned. */
    differs: boolean;
    /** It has lines and returned nothing at all. */
    nothingReturned: boolean;
    /** On the closing row of a refund that differs: what its lines add up to. */
    note: string | null;
}

// Per-location Orders tab embedded in the merchant management page. Reads the NEW event-sourced
// orders module (getLocationOrders + friends). Status is the SOURCE system's own lower-cased token,
// never a shared enum, so severity is derived heuristically — no fixed canonical map. Delivery status
// is '' (empty string) when absent. A synced order that never changed has an EMPTY timeline /
// status-history by design — that renders "no changes recorded", not an error.
//
// EVERY TOKEN ON THIS SCREEN IS RELABELLED THROUGH core/order-vocabulary — one map, no per-column
// prettifying. The stored vocabulary is untouched (those tokens are idempotency keys, Marten aliases and
// published lifecycle statuses), so the labels are strictly view-layer and each one carries its raw token
// in the tooltip: an operator reads the label, an engineer still debugs by token.
//
// Receipt rendering (paper template, CSS, print) lives in the shared OrderReceiptView component
// (components/order-receipt.ts) — this tab only HOSTS it, twice: as the detail modal's Receipt tab
// and as a per-row dialog so the paper is one click from the list.

@Component({
    selector: 'app-orders-tab',
    standalone: true,
    imports: [CardModule, ButtonModule, TableModule, TagModule, DialogModule, MessageModule, SelectModule, SelectButtonModule, InputTextModule, TooltipModule, FormsModule, DecimalPipe, DatePipe, OrderReceiptView],
    template: `
        @if (stats(); as s) {
            <div class="flex flex-wrap gap-6 mb-4 text-sm">
                <div><span class="text-surface-500 dark:text-surface-400">Orders</span> <span class="font-semibold tabular-nums ml-1">{{ s.orders }}</span></div>
                <div><span class="text-surface-500 dark:text-surface-400">Waiting to reach the POS</span> <span class="font-semibold tabular-nums ml-1">{{ s.pendingDispatch }}</span></div>
                <div><span class="text-surface-500 dark:text-surface-400">POS refused</span>
                    <span class="font-semibold tabular-nums ml-1" [class.text-red-500]="s.failedDispatch > 0">{{ s.failedDispatch }}</span>
                </div>
                @if (s.lastOrderPlacedAt) {
                    <div><span class="text-surface-500 dark:text-surface-400">Last order</span> <span class="ml-1">{{ s.lastOrderPlacedAt | date: 'short' }}</span></div>
                }
            </div>
        }

        <div class="flex flex-wrap items-center gap-2 mb-3">
            <input pInputText [(ngModel)]="search" (keyup.enter)="reload()" placeholder="Search ref / order # / customer" class="w-72" />
            <p-select [options]="statusOptions()" [(ngModel)]="status" (onChange)="reload()" placeholder="All statuses" [showClear]="true" styleClass="w-48" />
            <p-select [options]="sourceOptions()" [(ngModel)]="source" (onChange)="reload()" placeholder="All sources" [showClear]="true" styleClass="w-44" />
            <p-select [options]="deliveryOptions()" [(ngModel)]="deliveryStatus" (onChange)="reload()" placeholder="All deliveries" [showClear]="true" styleClass="w-48" />
            <p-button icon="pi pi-search" label="Search" size="small" (onClick)="reload()" />
            <p-button icon="pi pi-refresh" [text]="true" size="small" (onClick)="reload()" />
        </div>

        <p-table [value]="items()" [loading]="loading()" [lazy]="true" [paginator]="true" [rows]="pageSize"
                 [totalRecords]="total()" (onLazyLoad)="onPage($event)" [first]="first()">
            <ng-template pTemplate="header">
                <tr>
                    <th>External Ref</th><th>Source</th><th>Status</th><th>Payment</th><th>Customer</th>
                    <th>Grand total</th><th>Refunded</th><th>POS hand-off</th><th>Delivery</th><th>Placed</th><th></th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-o>
                <tr class="cursor-pointer" (click)="open(o)">
                    <td class="font-medium whitespace-nowrap">{{ o.externalOrderId }}
                        @if (o.orderNumber && o.orderNumber !== o.externalOrderId) { <span class="text-surface-400 text-xs">#{{ o.orderNumber }}</span> }
                    </td>
                    <td class="whitespace-nowrap"><p-tag [value]="label(o.source)" [pTooltip]="tooltip(o.source)" [severity]="sourceSev(o.source)" />
                        @if (o.printed) { <i class="pi pi-print text-surface-400 text-xs ml-1" title="Printed"></i> }
                    </td>
                    <!-- The SOURCE's status: what Magento said when it announced the order. With order-first
                         checkout that is before the card is charged, so it reads pending_payment and stays
                         there — which is why the money gets its own column beside it rather than being
                         inferred from this one. -->
                    <!-- The SOURCE's status. Relabelled when the ledger contradicts it: an order whose card
                         cleared but whose source still says pending_payment is not unpaid, it is waiting for
                         that system to confirm what already happened. Showing the raw token beside a green
                         "paid" read as a contradiction. -->
                    <td class="whitespace-nowrap">
                        <span [title]="statusHint(o)">
                            <p-tag [value]="statusLabel(o)" [severity]="rowStatusSev(o)" />
                        </span>
                    </td>
                    <td class="whitespace-nowrap">
                        @if (o.payment; as pay) {
                            <p-tag [value]="paymentLabel(pay)" [severity]="paymentSev(pay)" />
                            @if (pay.cardLast4) {
                                <span class="text-xs text-surface-500 ml-1">{{ pay.cardBrand }} ····{{ pay.cardLast4 }}</span>
                            }
                            @if (pay.isCaptured && !pay.matches) {
                                <!-- Captured a different amount than the order asks for: the one case here that
                                     needs a human, so it says the number rather than only flagging it. -->
                                <div class="text-xs text-red-500">captured {{ pay.capturedAmount | number: '1.2-2' }}</div>
                            }
                        } @else {
                            <span class="text-surface-400" title="No payment recorded — cash, another tender, or never paid">—</span>
                        }
                    </td>
                    <td>{{ o.customerName || '—' }}</td>
                    <td class="font-medium tabular-nums">{{ o.currency }} {{ o.grandTotal | number: '1.2-2' }}</td>
                    <td class="tabular-nums">@if (o.refundedTotal > 0) { <span class="text-amber-600 dark:text-amber-400">−{{ o.refundedTotal | number: '1.2-2' }}</span> } @else { <span class="text-surface-400">—</span> }</td>
                    <!-- OUR hand-off to the till, in operator English. The stored token rides along in the
                         tooltip — the label is what to read, the token is what to grep. -->
                    <td class="whitespace-nowrap"><p-tag [value]="label(o.dispatchStatus)" [pTooltip]="tooltip(o.dispatchStatus)" [severity]="dispatchSev(o.dispatchStatus)" /></td>
                    <!-- The COURIER's own status word, verbatim — DoorDash's vocabulary, not ours. Relabelling
                         it from our dictionary would mean guessing at another system's meaning. -->
                    <td>@if (o.deliveryStatus) { <p-tag [value]="o.deliveryStatus" [severity]="deliverySev(o.deliveryStatus)" /> } @else { <span class="text-surface-400">—</span> }</td>
                    <td class="text-sm text-surface-500 whitespace-nowrap">{{ o.placedAt | date: 'short' }}</td>
                    <td class="whitespace-nowrap">
                        <!-- Receipt straight from the list — same orders:read the list itself needs, so
                             no extra gating. stopPropagation: the row's own click opens the detail. -->
                        <p-button icon="pi pi-receipt" [text]="true" size="small" pTooltip="Receipt" tooltipPosition="left"
                                  (onClick)="openReceipt(o, $event)" />
                        <p-button icon="pi pi-eye" [text]="true" size="small" (onClick)="open(o)" />
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr><td colspan="11">{{ error() ?? 'No orders for this location yet.' }}</td></tr>
            </ng-template>
        </p-table>

        <p-dialog [(visible)]="showDetail" [modal]="true" [style]="{ width: '58rem' }" [dismissableMask]="true"
                  [header]="'Order ' + (selected()?.externalOrderId ?? '')">
            @if (detailError()) {
                <p-message severity="error">{{ detailError() }}</p-message>
            } @else if (detailLoading()) {
                <p class="text-sm text-surface-500">Loading…</p>
            } @else if (detail(); as d) {
                <div class="flex flex-wrap items-center gap-2 mb-4">
                    <p-tag [value]="d.state.status" [severity]="statusSev(d.state.status)" />
                    <p-tag [value]="label(d.source)" [pTooltip]="tooltip(d.source)" [severity]="sourceSev(d.source)" />
                    <span class="text-lg font-semibold tabular-nums">{{ d.state.currency }} {{ d.state.grandTotal | number: '1.2-2' }}</span>
                    @if (d.state.refundedTotal > 0) { <span class="text-amber-600 dark:text-amber-400 text-sm">refunded {{ d.state.refundedTotal | number: '1.2-2' }}</span> }
                    @if (d.state.balanceDue > 0) { <span class="text-surface-500 text-sm">· balance {{ d.state.balanceDue | number: '1.2-2' }}</span> }
                </div>

                <!-- The hand-off track's own note. It is an ERROR only when the send failed; a send the
                     intake payment gate is holding carries its REASON in the same field, and calling that an
                     error would report a deliberate wait as a defect. -->
                @if (d.lifecycle.lastDispatchError) {
                    @if (dispatchHeld(d.lifecycle.dispatchStatus)) {
                        <p-message severity="warn" styleClass="mb-3">Waiting before sending to the POS: {{ d.lifecycle.lastDispatchError }}</p-message>
                    } @else {
                        <p-message severity="error" styleClass="mb-3">POS hand-off error: {{ d.lifecycle.lastDispatchError }}</p-message>
                    }
                }

                <p-selectbutton [options]="tabs" [(ngModel)]="detailTab" (onChange)="onTabChange()" optionLabel="label" optionValue="value" [allowEmpty]="false" size="small" styleClass="mb-3" />

                @if (detailTab === 'summary') {
                    <div class="grid grid-cols-2 gap-2 mb-4 text-sm">
                        <div><b>Order #:</b> {{ d.state.orderNumber || '—' }}</div>
                        <div><b>Placed:</b> {{ d.placedAt | date: 'medium' }}</div>
                        <div><b>Customer:</b> {{ d.state.customerName || '—' }}</div>
                        <div><b>Email:</b> {{ d.state.customerEmail || '—' }}</div>
                        <div><b>Phone:</b> {{ d.state.customerPhone || '—' }}</div>
                        <div><b>Fulfillment:</b> {{ d.state.fulfillmentType || '—' }}</div>
                        <!-- Label FIRST, code SECOND — never label instead of code. The code is what XT,
                             Magento and the courier all match on, so hiding it would make this screen
                             useless for the one job it has (telling an operator which record to quote). -->
                        <div><b>Shipping method:</b> {{ shippingLabel(d.state.shippingMethod) || '—' }}
                            @if (showCode(d.state.shippingMethod, shippingLabel(d.state.shippingMethod))) {
                                <span class="text-surface-400 dark:text-surface-500 text-xs font-mono ml-1">{{ d.state.shippingMethod }}</span>
                            }
                        </div>
                        <div><b>Carrier:</b> {{ carrierLabel(d.state.carrier) || '—' }}
                            @if (showCode(d.state.carrier, carrierLabel(d.state.carrier))) {
                                <span class="text-surface-400 dark:text-surface-500 text-xs font-mono ml-1">{{ d.state.carrier }}</span>
                            }
                        </div>
                        <!-- The courier's own references. They live on the SNAPSHOT (the source told us),
                             unlike the delivery status below, which only a webhook can move — so an empty
                             one here means the source had not attached the delivery when we read the order,
                             not that a courier update went missing. -->
                        <!-- SNAPSHOT FIRST, THEN OUR OWN SHIPMENT. These two fields are filled from the
                             source's payload, and for a middleware-committed courier the source cannot know
                             either — WE booked it. So the fallback is the newest shipment recorded here, which
                             is where the accept's tracking lands. Without it this header read "not provided by
                             the source yet" while the Shipments table below showed the reference and the
                             number. -->
                        <div><b>Delivery ref:</b>
                            @if (d.state.externalDeliveryId || ourDeliveryRef(d)) { <span class="font-mono text-xs">{{ d.state.externalDeliveryId || ourDeliveryRef(d) }}</span> }
                            @else { <span class="text-surface-400 dark:text-surface-500 italic">{{ deliveryRefAbsence(d) }}</span> }
                        </div>
                        <div><b>Tracking:</b>
                            @if (d.state.trackingUrl || ourTrackingUrl(d)) {
                                <a [href]="d.state.trackingUrl || ourTrackingUrl(d)" target="_blank" rel="noopener noreferrer" class="text-primary font-medium">Track <i class="pi pi-external-link text-xs"></i></a>
                            } @else if (d.state.supportReference || ourTrackingNumber(d)) {
                                <span class="font-mono text-xs">{{ d.state.supportReference || ourTrackingNumber(d) }}</span>
                            } @else {
                                <span class="text-surface-400 dark:text-surface-500 italic">{{ deliveryRefAbsence(d) }}</span>
                            }
                            @if (d.state.trackingUrl && d.state.supportReference) {
                                <span class="text-surface-400 dark:text-surface-500 text-xs font-mono ml-2">{{ d.state.supportReference }}</span>
                            }
                        </div>
                        <!-- The customer's checkout note, delivery or pickup. It already went to XT and to the
                             store Edge; on 2026-09-24 a QA operator could not tell whether it had reached the
                             middleware at all, because nothing here showed it. "" is the server saying the
                             order carries none; null is an order last recorded before the field existed, which
                             fills in on its next snapshot - saying "none" there would be a claim we cannot make. -->
                        <div class="col-span-2"><b>Special instructions:</b>
                            @if (d.state.deliveryComment) { <span class="whitespace-pre-line">{{ d.state.deliveryComment }}</span> }
                            @else if (d.state.deliveryComment === '') { <span class="text-surface-400 dark:text-surface-500 italic">none left at checkout</span> }
                            @else { <span class="text-surface-400 dark:text-surface-500 italic">not recorded yet (order last read before notes were kept)</span> }
                        </div>
                        <div><b>Seller:</b> {{ d.state.sellerName || '—' }}</div>
                        <div><b>Store code:</b> {{ d.state.storeCode || '—' }}</div>
                        <div><b>Contains liquor:</b> {{ d.state.containsLiquor ? 'Yes' : 'No' }}</div>
                        <div><b>Items:</b> {{ d.state.items.length }}</div>
                    </div>

                    <!-- THE MONEY, IN THREE COLUMNS: the goods, what the customer paid on top of them, and how it was
                         settled. Each charge on top is NAMED - the delivery charge is Magento's shipping amount plus any
                         fee row the merchant's fee management maps to the delivery fee; the other rows keep the label
                         the merchant gave them with their XT bucket as a hint - because "Fees 9.75" answered nothing
                         when a store asked what the 9.75 was. -->
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-sm bg-surface-50 dark:bg-surface-800 rounded p-3">
                        <div>
                            <div class="text-surface-500 uppercase text-xs tracking-wide mb-1">Goods</div>
                            <div class="flex justify-between gap-3"><span>Subtotal</span><span class="tabular-nums">{{ d.state.subtotal | number: '1.2-2' }}</span></div>
                            @if (d.state.discountTotal) {
                                <div class="flex justify-between gap-3"><span>Discount</span><span class="tabular-nums">−{{ d.state.discountTotal | number: '1.2-2' }}</span></div>
                            }
                            <div class="flex justify-between gap-3"><span>Tax</span><span class="tabular-nums">{{ d.state.taxTotal | number: '1.2-2' }}</span></div>
                        </div>
                        <div>
                            <div class="text-surface-500 uppercase text-xs tracking-wide mb-1">On top of the goods</div>
                            <div class="flex justify-between gap-3">
                                <span>Delivery @if (deliveryHint(d.state); as hint) { <span class="text-surface-400 dark:text-surface-500 text-xs">{{ hint }}</span> }</span>
                                <span class="tabular-nums">{{ deliveryTotal(d.state) | number: '1.2-2' }}</span>
                            </div>
                            @for (f of otherFees(d.state); track f.code + f.label) {
                                <div class="flex justify-between gap-3">
                                    <span>{{ f.label || f.code || 'Fee' }} <span class="text-surface-400 dark:text-surface-500 text-xs">{{ feeKind(f) }}</span></span>
                                    <span class="tabular-nums">{{ f.amount | number: '1.2-2' }}</span>
                                </div>
                            }
                            @if (unnamedFees(d.state); as rest) {
                                <div class="flex justify-between gap-3">
                                    <span>Fees <span class="text-surface-400 dark:text-surface-500 text-xs">not itemised by the channel</span></span>
                                    <span class="tabular-nums">{{ rest | number: '1.2-2' }}</span>
                                </div>
                            }
                            <div class="flex justify-between gap-3"><span>Tip</span><span class="tabular-nums">{{ d.state.tipTotal | number: '1.2-2' }}</span></div>
                        </div>
                        <div>
                            <div class="text-surface-500 uppercase text-xs tracking-wide mb-1">Settlement</div>
                            <div class="flex justify-between gap-3 font-semibold"><span>Grand total</span><span class="tabular-nums">{{ d.state.grandTotal | number: '1.2-2' }}</span></div>
                            <div class="flex justify-between gap-3"><span>Amount paid</span><span class="tabular-nums">{{ d.state.amountPaid | number: '1.2-2' }}</span></div>
                            @if (d.state.refundedTotal) {
                                <div class="flex justify-between gap-3 text-amber-600 dark:text-amber-400"><span>Refunded</span><span class="tabular-nums">−{{ d.state.refundedTotal | number: '1.2-2' }}</span></div>
                            }
                            @if (d.state.balanceDue) {
                                <div class="flex justify-between gap-3"><span>Balance due</span><span class="tabular-nums">{{ d.state.balanceDue | number: '1.2-2' }}</span></div>
                            }
                        </div>
                    </div>

                    <h6>Lifecycle</h6>
                    <!-- Every field here is written ONLY by a lifecycle fact, so an empty one means "no such
                         fact has arrived" — which is not the same statement as "the value was lost". An
                         em-dash reads as the second and sends an operator hunting a bug that isn't there, so
                         each blank says WHY it is blank instead. -->
                    <div class="grid grid-cols-2 gap-2 mb-4 text-sm">
                        <div class="flex items-center gap-2"><b>POS hand-off:</b> <p-tag [value]="label(d.lifecycle.dispatchStatus)" [pTooltip]="tooltip(d.lifecycle.dispatchStatus)" [severity]="dispatchSev(d.lifecycle.dispatchStatus)" /></div>
                        <div><b>Sent to:</b>
                            @if (d.lifecycle.dispatchRecipient; as rcp) { <span [title]="tooltip(rcp)">{{ label(rcp) }}</span> }
                            @else { <span class="text-surface-400 dark:text-surface-500 italic">{{ recipientAbsence(d) }}</span> }
                        </div>
                        <div class="flex items-center gap-2"><b>Courier:</b>
                            @if (d.lifecycle.deliveryStatus) { <p-tag [value]="d.lifecycle.deliveryStatus" [severity]="deliverySev(d.lifecycle.deliveryStatus)" /> }
                            @else { <span class="text-surface-400 dark:text-surface-500 italic">{{ deliveryAbsence(d) }}</span> }
                        </div>
                        <div><b>Fulfillment stage:</b>
                            @if (d.lifecycle.fulfillmentStage; as stage) { <span [title]="tooltip(stage)">{{ label(stage) }}</span> }
                            @else { <span class="text-surface-400 dark:text-surface-500 italic">no stage recorded</span> }
                        </div>
                        <div><b>POS order number:</b>
                            @if (d.lifecycle.xtOrderId) { {{ d.lifecycle.xtOrderId }} }
                            @else { <span class="text-surface-400 dark:text-surface-500 italic">{{ posOrderNumberAbsence(d) }}</span> }
                        </div>
                        <!-- Printing is a RECORD the store's print station later FETCHES, so this row can
                             say more than yes/no: whether a ticket exists at all, and whether one is
                             sitting queued for the printer's next poll (the state a re-print re-arms).
                             The tag stays the lifecycle's own printed fact — history — while the queued
                             state and the absence notes come from the live print record. -->
                        <div class="flex items-center gap-2 flex-wrap col-span-2"><b>Printed:</b>
                            <p-tag [value]="d.lifecycle.printed ? 'Yes' : 'No'" [severity]="d.lifecycle.printed ? 'success' : 'secondary'" />
                            @if (d.lifecycle.printedAt) {
                                <span class="text-surface-500 text-xs">{{ d.lifecycle.printedAt | date: 'short' }}@if (d.lifecycle.printedBy) { · {{ d.lifecycle.printedBy }}}</span>
                            }
                            @if (printState(); as ps) {
                                @if (ps.pendingPrint) {
                                    <p-tag value="queued for printer" severity="info" />
                                } @else if (!ps.ticketExists && startedAtPointOfSale()) {
                                    <span class="text-surface-400 dark:text-surface-500 italic text-sm">no print ticket — a sale taken at the store's counter is printed there, not here</span>
                                } @else if (!ps.ticketExists) {
                                    <span class="text-surface-400 dark:text-surface-500 italic text-sm">no print ticket — instant printing was off for this location when the order arrived</span>
                                } @else if (!ps.instantPrintEnabled) {
                                    <span class="text-surface-400 dark:text-surface-500 italic text-sm">ticket exists, but instant printing is now disabled</span>
                                }
                                @if (canReprint() && !startedAtPointOfSale()) {
                                    @if (!confirmReprint()) {
                                        <p-button [label]="ps.ticketExists ? 'Re-print' : 'Print'" icon="pi pi-print" size="small" [outlined]="true"
                                                  [disabled]="reprinting()" (onClick)="confirmReprint.set(true)" />
                                    } @else {
                                        <span class="text-sm">Queue this order for the store's printer?</span>
                                        <p-button label="Confirm" icon="pi pi-print" size="small" severity="warn"
                                                  [loading]="reprinting()" (onClick)="reprint()" />
                                        <p-button label="Cancel" size="small" [text]="true" (onClick)="confirmReprint.set(false)" />
                                    }
                                }
                            }
                        </div>
                    </div>

                    <!-- The refusal, verbatim: nothing will come out of the printer, and saying why beats
                         a success toast over a silent print station. -->
                    @if (reprintError()) { <p-message severity="error" styleClass="mb-3">{{ reprintError() }}</p-message> }
                    @if (reprintResult(); as rp) {
                        <p-message severity="success" styleClass="mb-3">
                            Print ticket {{ rp.created ? 'composed and queued' : 're-queued' }} — the store's printer picks it up shortly.
                        </p-message>
                    }

                    <!-- Shipments sit with Lifecycle rather than with Items because a shipment is a fact
                         about what the platform DID with the order, not part of the source system's canonical
                         state. The empty case used to hide the whole section, which made "nothing has
                         shipped" and "this screen forgot to render shipments" look identical — so it now
                         states which one it is. -->
                    <h6>Shipments</h6>
                    @if (d.lifecycle.shipments?.length) {
                        <p-table [value]="d.lifecycle.shipments!" styleClass="p-datatable-sm mb-4">
                            <ng-template pTemplate="header">
                                <tr><th>Reference</th><th>Carrier</th><th>Method</th><th>Tracking</th><th>Shipped</th><th>Tracking updated</th></tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-s>
                                <tr>
                                    <td class="text-sm font-medium whitespace-nowrap">{{ s.shipmentReference || '—' }}</td>
                                    <!-- Same carrier map the Summary header uses — one dictionary, not one per table. -->
                                    <td class="text-sm">{{ carrierLabel(s.carrier) || '—' }}</td>
                                    <td class="text-sm">{{ s.shippingMethod || '—' }}</td>
                                    <!-- A shipment is routinely recorded before the carrier issues a number, so
                                         "no tracking" is a PENDING state, not a missing value: an em-dash here
                                         would read as an error and send an operator chasing a non-problem. -->
                                    <td class="text-sm">
                                        @if (s.trackingUrl) {
                                            <a [href]="s.trackingUrl" target="_blank" rel="noopener noreferrer" class="text-primary font-medium">
                                                {{ s.trackingNumber || 'Track' }} <i class="pi pi-external-link text-xs"></i>
                                            </a>
                                        } @else if (s.trackingNumber) {
                                            <span class="tabular-nums">{{ s.trackingNumber }}</span>
                                        } @else {
                                            <p-tag value="pending" severity="info" />
                                        }
                                    </td>
                                    <td class="text-sm text-surface-500 whitespace-nowrap">{{ s.shippedAt | date: 'short' }}</td>
                                    <td class="text-sm text-surface-500 whitespace-nowrap">{{ s.trackingUpdatedAt ? (s.trackingUpdatedAt | date: 'short') : '—' }}</td>
                                </tr>
                                <!-- Lines are optional per shipment: a courier taking the whole order reports
                                     none, while a partial parcel reports exactly what it covers. Rendered as an
                                     expansion row (same idiom as the timeline's detail row) so a shipment with
                                     no breakdown costs no vertical space. -->
                                @if (s.lines?.length) {
                                    <tr><td colspan="6" class="text-xs text-surface-500 dark:text-surface-400 pl-4">
                                        @for (l of s.lines; track $index) { <span class="mr-3">{{ l.sku }} × {{ l.quantity }}</span> }
                                    </td></tr>
                                }
                            </ng-template>
                        </p-table>
                    } @else {
                        <p class="text-sm text-surface-500 dark:text-surface-400 mb-4 italic">{{ shipmentsAbsence(d) }}</p>
                    }

                    <h6>Items</h6>
                    <!-- THE ROW TOTAL IS WHAT THE LINE COST THE CUSTOMER: goods less discount plus tax, the way
                         Magento's own admin shows a row (row_total - discount + tax) and what a refund of the line
                         gives back. The stored lineTotal is Magento's row_total - the goods alone, before discount
                         and tax - and shown under "Line total" beside a Tax column it read as wrong (249.99 next
                         to 20.00 of tax, 2026-10-09). Goods stay readable as Unit x Qty. -->
                    <p-table [value]="d.state.items" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header">
                            <tr><th>SKU</th><th>Name</th><th class="text-right">Qty</th><th class="text-right">Unit</th><th class="text-right">Disc</th><th class="text-right">Tax</th><th class="text-right" title="Unit x Qty, less the discount, plus the tax: what the line cost">Row total</th></tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-it>
                            <tr>
                                <td class="text-sm">{{ it.sku }}</td>
                                <td>{{ it.name }}</td>
                                <td class="text-right tabular-nums">{{ it.quantity }}</td>
                                <td class="text-right tabular-nums">{{ it.unitPrice | number: '1.2-2' }}</td>
                                <td class="text-right tabular-nums">{{ it.discount | number: '1.2-2' }}</td>
                                <td class="text-right tabular-nums">{{ it.taxAmount | number: '1.2-2' }}</td>
                                <td class="text-right tabular-nums font-medium" [title]="'Goods ' + (it.lineTotal | number: '1.2-2')">{{ rowTotal(it) | number: '1.2-2' }}</td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage"><tr><td colspan="7">No line items.</td></tr></ng-template>
                    </p-table>

                    @if (d.state.payments.length) {
                        <h6 class="mt-4">Payments</h6>
                        <p-table [value]="d.state.payments" styleClass="p-datatable-sm">
                            <ng-template pTemplate="header">
                                <tr><th>Method</th><th>Kind</th><th>Status</th><th>Card</th><th class="text-right">Amount</th><th>Paid</th></tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-p>
                                <tr>
                                    <td>{{ p.method }}</td>
                                    <td class="text-sm">{{ p.kind }}</td>
                                    <td><p-tag [value]="p.status" [severity]="statusSev(p.status)" /></td>
                                    <td class="text-sm">@if (p.cardLast4) { {{ p.cardBrand }} •••• {{ p.cardLast4 }} } @else { <span class="text-surface-400">—</span> }</td>
                                    <td class="text-right tabular-nums">{{ p.amount | number: '1.2-2' }}</td>
                                    <td class="text-sm text-surface-500">{{ p.paidAt ? (p.paidAt | date: 'short') : '—' }}</td>
                                </tr>
                            </ng-template>
                        </p-table>
                    }

                    @if (d.state.refunds.length) {
                        <h6 class="mt-4">Refunds</h6>
                        <!-- ONE ROW PER LINE A REFUND GAVE BACK - each product by its name, each fee by what it is -
                             under the refund's date, reason and status, and the refund's own total closing the
                             group when it is more than one line. Ours: written by the middleware from the refund
                             it carried out, never fetched from Magento.
                             A REFUND WHOSE LINES AND TOTAL DISAGREE SAYS SO: its closing row reads "Returned" with
                             what the lines add up to beneath it, and one that returned nothing is tagged as that
                             with its lines greyed - a tip listed at 26.19 over a total of 0.00 read as a tip
                             refunded. -->
                        <p-table [value]="refundRows(d.state.refunds)" styleClass="p-datatable-sm">
                            <ng-template pTemplate="header">
                                <tr><th>Refunded</th><th>Reason</th><th>Status</th><th>Item</th><th class="text-right">Qty</th><th class="text-right">Amount</th></tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-row>
                                <tr [class.font-semibold]="row.isTotal">
                                    <td class="text-sm text-surface-500 whitespace-nowrap">@if (row.first) { {{ row.refundedAt ? (row.refundedAt | date: 'short') : '—' }} }</td>
                                    <td class="text-sm">@if (row.first) { {{ row.reason || '—' }} }</td>
                                    <td>
                                        @if (row.first) {
                                            @if (row.nothingReturned) { <p-tag value="nothing returned" severity="danger" /> }
                                            @else { <p-tag [value]="row.status" [severity]="statusSev(row.status)" /> }
                                        }
                                    </td>
                                    <td class="text-sm" [class.text-surface-500]="row.kind === 'fee'" [class.pl-6]="!row.isTotal">
                                        {{ row.item }}
                                        @if (row.note) { <span class="block text-xs font-normal text-amber-600 dark:text-amber-400">{{ row.note }}</span> }
                                    </td>
                                    <td class="text-right tabular-nums text-sm">{{ row.qty ?? '' }}</td>
                                    <td class="text-right tabular-nums">
                                        @if (row.nothingReturned && !row.isTotal) { <span class="text-surface-400 line-through">{{ row.amount | number: '1.2-2' }}</span> }
                                        @else if (row.differs && row.isTotal) { <span class="text-amber-600 dark:text-amber-400">{{ row.amount | number: '1.2-2' }}</span> }
                                        @else { {{ row.amount | number: '1.2-2' }} }
                                    </td>
                                </tr>
                            </ng-template>
                        </p-table>
                    }
                }

                @if (detailTab === 'receipt') {
                    <!-- Thin host: the shared component owns loading, the paper, and Print. It mounts
                         when the tab opens, so the fetch stays as lazy as the other tabs'. -->
                    @if (selected(); as sel) {
                        <app-order-receipt [locationId]="locationId()" [externalOrderId]="sel.externalOrderId" [source]="sel.source" />
                    }
                }

                @if (detailTab === 'timeline') {
                    @if (timelineLoading()) {
                        <p class="text-sm text-surface-500">Loading timeline…</p>
                    } @else if (timeline().length) {
                        <p-table [value]="timeline()" styleClass="p-datatable-sm">
                            <ng-template pTemplate="header">
                                <tr><th>When</th><th>Category</th><th>Change</th><th>Field</th><th>From</th><th>To</th></tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-t>
                                <tr>
                                    <td class="text-sm text-surface-500 whitespace-nowrap">{{ t.at | date: 'short' }}</td>
                                    <td><p-tag [value]="label(t.category)" [pTooltip]="tooltip(t.category)" [severity]="t.category === 'Lifecycle' ? 'info' : 'secondary'" /></td>
                                    <td class="text-sm"><span [title]="tooltip(t.changeType)">{{ label(t.changeType) }}</span></td>
                                    <td class="text-sm">{{ t.field ?? '—' }}</td>
                                    <td class="text-sm text-surface-500">{{ display(t, 'old') }}</td>
                                    <td class="text-sm">{{ display(t, 'new') }}</td>
                                </tr>
                                @if (t.detail) {
                                    <tr><td colspan="6" class="text-xs text-surface-500 dark:text-surface-400 pl-4">{{ t.detail }}@if (t.reportedBy) { <span class="text-surface-400"> · by {{ label(t.reportedBy) }}</span> }</td></tr>
                                }
                            </ng-template>
                        </p-table>
                        @if (timelineTotal() > timeline().length) {
                            <p class="text-xs text-surface-400 mt-2">Showing first {{ timeline().length }} of {{ timelineTotal() }} entries.</p>
                        }
                    } @else {
                        <p class="text-sm text-surface-500">No changes recorded.</p>
                    }
                }

                @if (detailTab === 'events') {
                    @if (streamEventsLoading()) {
                        <p class="text-sm text-surface-500">Loading events…</p>
                    } @else {
                        <p-table [value]="streamEvents()" styleClass="p-datatable-sm">
                            <ng-template pTemplate="header">
                                <tr><th class="w-12">#</th><th>Event</th><th>When</th><th></th></tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-ev let-i="rowIndex">
                                <tr>
                                    <td class="text-surface-500">{{ ev.version }}</td>
                                    <!-- The fact, not its envelope: an operation row is tagged with the operation's own
                                         name ("Credit memo issued", "Card refunded"), every row carries one line of what
                                         it says, and the STORED alias stays on the tooltip and in the expanded body,
                                         because that string is what the store rehydrates by. -->
                                    <td>
                                        <p-tag [value]="streamEventLabel(ev)" [pTooltip]="streamEventTooltip(ev)" severity="info" />
                                        @if (ev.headline) { <span class="ml-2 text-sm">{{ ev.headline }}</span> }
                                    </td>
                                    <td class="text-sm text-surface-500">{{ ev.at | date: 'medium' }}</td>
                                    <td><p-button [icon]="expandedStreamEvent() === i ? 'pi pi-chevron-up' : 'pi pi-code'" [text]="true" size="small" (onClick)="toggleStreamEvent(i)" /></td>
                                </tr>
                                @if (expandedStreamEvent() === i) {
                                    <tr>
                                        <td colspan="4">
                                            <pre class="text-xs bg-surface-100 dark:bg-surface-800 rounded p-3 overflow-x-auto whitespace-pre-wrap max-h-96">{{ prettyStreamEvent(ev) }}</pre>
                                        </td>
                                    </tr>
                                }
                            </ng-template>
                            <ng-template pTemplate="emptymessage"><tr><td colspan="4">No events recorded for this order.</td></tr></ng-template>
                        </p-table>
                    }
                }

                @if (detailTab === 'delivery') {
                    @if (deliveryLoading()) {
                        <p class="text-sm text-surface-500">Loading delivery…</p>
                    } @else if (delivery(); as dv) {
                        <div class="flex flex-wrap items-center gap-2 mb-3 text-sm bg-surface-50 dark:bg-surface-800 rounded p-3">
                            @if (dv.provider) { <p-tag [value]="label(dv.provider)" [pTooltip]="tooltip(dv.provider)" severity="info" /> } @else { <span class="text-surface-500">No courier on this order</span> }
                            @if (dv.status) { <p-tag [value]="dv.status" [severity]="deliverySev(dv.status)" /> }
                            @if (dv.fulfillmentType) { <span class="text-surface-500">{{ dv.fulfillmentType }}</span> }
                            @if (dv.externalDeliveryId) { <span class="font-mono text-xs text-surface-500">{{ dv.externalDeliveryId }}</span> }
                        </div>

                        <div class="grid grid-cols-2 gap-2 mb-3 text-sm">
                            <div><b>Tracking:</b>
                                <!-- The link a customer asking "where is my order?" needs. It arrives on the
                                     courier's webhooks, not with the order, so an order can legitimately have
                                     none yet — say so rather than render a dead link. -->
                                @if (dv.trackingUrl) {
                                    <a [href]="dv.trackingUrl" target="_blank" rel="noopener noreferrer" class="text-primary underline break-all">Open live tracking</a>
                                } @else { <span class="text-surface-400">not provided yet</span> }
                            </div>
                            <div><b>Support ref:</b> <span class="font-mono text-xs">{{ dv.supportReference || '—' }}</span></div>
                            <div><b>Driver:</b>
                                @if (dv.courier?.name || dv.courier?.phone) {
                                    {{ dv.courier?.name || 'Unnamed' }}
                                    @if (dv.courier?.phone) { <span class="text-surface-500">· {{ dv.courier?.phone }}</span> }
                                    @if (dv.courier?.vehicle) { <span class="text-surface-500">· {{ dv.courier?.vehicle }}</span> }
                                } @else { <span class="text-surface-400">not assigned yet</span> }
                            </div>
                            <div><b>Webhooks:</b> <span class="tabular-nums">{{ dv.webhooksReceived }}</span> received
                                <!-- Pending means the courier told us something the order never acted on —
                                     usually a webhook that arrived before the order existed. -->
                                @if (dv.webhooksPending > 0) {
                                    <span class="text-amber-600 dark:text-amber-400">· {{ dv.webhooksPending }} pending</span>
                                }
                            </div>
                        </div>

                        <div class="flex items-center gap-2 mb-3">
                            <p-button label="Replay webhooks" icon="pi pi-replay" size="small" [outlined]="true"
                                      [loading]="replaying()" (onClick)="replayDelivery()" />
                            <i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Safe to repeat: updates already applied are skipped." tooltipPosition="top"></i>
                        </div>

                        @if (replayError()) { <p-message severity="error" styleClass="mb-3">{{ replayError() }}</p-message> }
                        @if (replayResult(); as rr) {
                            <p-message [severity]="rr.failed > 0 ? 'warn' : 'success'" styleClass="mb-3">
                                Replayed {{ rr.total }}: {{ rr.applied }} applied, {{ rr.skipped }} already done, {{ rr.failed }} failed.
                            </p-message>
                        }

                        @if (dv.updates.length) {
                            <p-table [value]="dv.updates" styleClass="p-datatable-sm">
                                <ng-template pTemplate="header">
                                    <tr><th>When</th><th>Reported by</th><th>Status</th><th>Detail</th><th class="w-10"></th></tr>
                                </ng-template>
                                <ng-template pTemplate="body" let-u let-i="rowIndex">
                                    <tr>
                                        <td class="text-sm text-surface-500 whitespace-nowrap">{{ u.occurredAt | date: 'short' }}</td>
                                        <td class="text-sm">{{ label(u.provider) }}</td>
                                        <td><p-tag [value]="u.status" [severity]="deliveryUpdateSev(u.status)" /></td>
                                        <td class="text-sm text-surface-500">
                                            {{ u.comment || '—' }}
                                            @if (u.cancellationReason) { <span class="text-red-500">({{ u.cancellationReason }})</span> }
                                        </td>
                                        <td>
                                            @if (u.payload) {
                                                <p-button [icon]="expandedDeliveryUpdate() === i ? 'pi pi-chevron-up' : 'pi pi-code'"
                                                          [text]="true" size="small" (onClick)="toggleDeliveryUpdate(i)" />
                                            }
                                        </td>
                                    </tr>
                                    @if (expandedDeliveryUpdate() === i && u.payload) {
                                        <tr>
                                            <td colspan="5">
                                                <!-- The courier's own body, verbatim. -->
                                                <pre class="text-xs bg-surface-100 dark:bg-surface-800 rounded p-3 overflow-x-auto whitespace-pre-wrap max-h-96">{{ prettyPayload(u.payload) }}</pre>
                                            </td>
                                        </tr>
                                    }
                                </ng-template>
                            </p-table>
                        } @else {
                            <p class="text-sm text-surface-500">
                                No courier updates recorded for this order.
                                @if (dv.webhooksReceived > 0) { Webhooks were received but never applied — try Replay above. }
                            </p>
                        }
                    } @else {
                        <p class="text-sm text-surface-500">{{ replayError() ?? 'No delivery information for this order.' }}</p>
                    }
                }

                @if (detailTab === 'dispatches') {
                    <div class="flex items-center gap-2 mb-3">
                        @if (canSendXt()) {
                            @if (!confirmSendXt()) {
                                <p-button label="Send to XT (legacy)" icon="pi pi-send" size="small" [outlined]="true"
                                          [disabled]="sendingXt()" (onClick)="confirmSendXt.set(true)" />
                            } @else {
                                <span class="text-sm">Queue a send of this order to XT?
                                    <span class="text-surface-500">If XT already has this order, nothing is re-sent.</span>
                                </span>
                                <p-button label="Confirm" icon="pi pi-send" size="small" severity="warn"
                                          [loading]="sendingXt()" (onClick)="sendToXt()" />
                                <p-button label="Cancel" size="small" [text]="true" (onClick)="confirmSendXt.set(false)" />
                            }
                        }
                        @if (canCompleteXt()) {
                            @if (!confirmCompleteXt()) {
                                <p-button label="Mark complete in XT" icon="pi pi-check-circle" size="small" [outlined]="true"
                                          [disabled]="completingXt()" (onClick)="confirmCompleteXt.set(true)" />
                            } @else {
                                <span class="text-sm">Mark this order complete in the POS?
                                    <span class="text-surface-500">Use for pickups, or when XT refused the completion.</span>
                                </span>
                                <p-button label="Confirm" icon="pi pi-check-circle" size="small" severity="warn"
                                          [loading]="completingXt()" (onClick)="completeInXt()" />
                                <p-button label="Cancel" size="small" [text]="true" (onClick)="confirmCompleteXt.set(false)" />
                            }
                        }
                        <!-- The evidence that replaces the hidden levers: the chip says XT has the order
                             (why "Send to XT" is gone), the complete tag says the POS was told it is done
                             (why "Mark complete" is gone). Both are read from the same records the tables
                             below render, so a hidden lever is always explainable from this row. -->
                        @if (canSendEdge()) {
                            @if (!confirmSendEdge()) {
                                <p-button label="Send to store" icon="pi pi-shop" size="small"
                                          [disabled]="sendingEdge()" (onClick)="confirmSendEdge.set(true)" />
                            } @else {
                                <span class="text-sm">Send this order to the store Edge again?
                                    <span class="text-surface-500">If the store refused it, its operators are asked again.</span>
                                </span>
                                <p-button label="Confirm" icon="pi pi-shop" size="small" severity="warn"
                                          [loading]="sendingEdge()" (onClick)="sendToEdge()" />
                                <p-button label="Cancel" size="small" [text]="true" (onClick)="confirmSendEdge.set(false)" />
                            }
                        }
                        <!-- The store-side twins of the XT levers: tell the store Edge where the order got to
                             (a carrier shipment with its tracking, a pickup collected, a phone order finished),
                             or tell it to stop. Both go to the store's queue and are recorded on the order. -->
                        @if (canUpdateStore() && !showStoreUpdate()) {
                            <p-button label="Update store" icon="pi pi-truck" size="small" [outlined]="true"
                                      [disabled]="updatingStore()" (onClick)="openStoreUpdate()" />
                        }
                        @if (canCancelAtStore()) {
                            @if (!confirmCancelStore()) {
                                <p-button label="Cancel at store" icon="pi pi-ban" size="small" [outlined]="true" severity="danger"
                                          [disabled]="cancellingStore()" (onClick)="confirmCancelStore.set(true)" />
                            } @else {
                                <span class="text-sm">Tell the store to stop working on this order?
                                    <span class="text-surface-500">Store only — nothing changes in Magento and no money moves.</span>
                                </span>
                                <input pInputText [(ngModel)]="storeCancelReason" placeholder="Reason (optional)" class="w-64" />
                                <p-button label="Confirm" icon="pi pi-ban" size="small" severity="danger"
                                          [loading]="cancellingStore()" (onClick)="cancelAtStore()" />
                                <p-button label="Cancel" size="small" [text]="true" (onClick)="confirmCancelStore.set(false)" />
                            }
                        }
                        @if (dispatches()?.xtOrderId; as xtId) {
                            <p-tag [value]="'POS order ' + xtId" severity="success" />
                        }
                        @if (xtCompleteRecorded()) {
                            <p-tag value="complete in the POS" severity="success" />
                        }
                        @if (storeCompleteRecorded()) {
                            <p-tag value="complete at the store" severity="success" />
                        }
                    </div>

                    @if (showStoreUpdate()) {
                        <div class="flex flex-wrap items-center gap-2 mb-3">
                            <span class="text-sm">Tell the store the order is</span>
                            <p-select [options]="storeStatusOptions()" [(ngModel)]="storeStatus" optionLabel="label" optionValue="value"
                                      styleClass="w-44" size="small" />
                            @if (storeStatus === 'shipped' || storeStatus === 'out') {
                                <input pInputText [(ngModel)]="storeCarrier" placeholder="Carrier" class="w-32" />
                                <input pInputText [(ngModel)]="storeTrackingNumber" placeholder="Tracking number" class="w-44" />
                                <input pInputText [(ngModel)]="storeTrackingUrl" placeholder="Tracking link" class="w-64" />
                            }
                            <input pInputText [(ngModel)]="storeNote" placeholder="Note for the counter (optional)" class="w-64" />
                            <p-button label="Send to store" icon="pi pi-truck" size="small" severity="warn"
                                      [loading]="updatingStore()" (onClick)="updateStore()" />
                            <p-button label="Cancel" size="small" [text]="true" (onClick)="showStoreUpdate.set(false)" />
                        </div>
                    }
                    @if (storeUpdateError()) { <p-message severity="error" styleClass="mb-3">{{ storeUpdateError() }}</p-message> }
                    @if (storeUpdateSuccess(); as msg) { <p-message severity="success" styleClass="mb-3">{{ msg }}</p-message> }
                    @if (storeCancelError()) { <p-message severity="error" styleClass="mb-3">{{ storeCancelError() }}</p-message> }
                    @if (storeCancelSuccess(); as msg) { <p-message severity="success" styleClass="mb-3">{{ msg }}</p-message> }

                    @if (sendXtError()) { <p-message severity="error" styleClass="mb-3">{{ sendXtError() }}</p-message> }
                    @if (sendXtSuccess(); as msg) { <p-message severity="success" styleClass="mb-3">{{ msg }}</p-message> }
                    @if (sendEdgeError()) { <p-message severity="error" styleClass="mb-3">{{ sendEdgeError() }}</p-message> }
                    @if (sendEdgeSuccess(); as msg) { <p-message severity="success" styleClass="mb-3">{{ msg }}</p-message> }
                    <!-- XT's own refusal, verbatim: the POS did NOT change, and saying so beats a
                         success toast over an order that is still out for delivery in the store. -->
                    @if (completeXtError()) { <p-message severity="error" styleClass="mb-3">{{ completeXtError() }}</p-message> }
                    @if (completeXtSuccess(); as msg) { <p-message severity="success" styleClass="mb-3">{{ msg }}</p-message> }

                    @if (dispatches()?.payment; as pay) {
                        <!-- The payment that funded this order — the same facts the dispatch hands back to
                             Magento's Payment Information block. -->
                        <div class="flex flex-wrap items-center gap-2 mb-3 text-sm bg-surface-50 dark:bg-surface-800 rounded p-3">
                            <b>Payment:</b>
                            <p-tag [value]="pay.status" [severity]="pay.isCaptured ? 'success' : 'warn'" />
                            @if (pay.cardBrand || pay.cardLast4) {
                                <span>{{ pay.cardBrand || 'Card' }} ••••{{ pay.cardLast4 || '????' }}</span>
                            }
                            <span class="tabular-nums">captured {{ pay.capturedAmount | number: '1.2-2' }} {{ pay.currency }}</span>
                            @if (pay.transactionId) {
                                <span class="font-mono text-xs text-surface-500">{{ pay.transactionId }}</span>
                            }
                        </div>
                    }

                    @if (dispatchesLoading()) {
                        <p class="text-sm text-surface-500">Loading hand-off records…</p>
                    } @else if (dispatches()?.dispatches?.length) {
                        <p-table [value]="dispatches()!.dispatches" styleClass="p-datatable-sm">
                            <ng-template pTemplate="header">
                                <tr><th>Sent to</th><th>State</th><th>Attempts</th><th>Queued</th><th>Accepted</th><th>Last error</th></tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-r>
                                <tr>
                                    <td><span [title]="tooltip(r.recipient)">{{ label(r.recipient) }}</span></td>
                                    <td><p-tag [value]="label(r.status)" [pTooltip]="tooltip(r.status)" [severity]="dispatchSev(r.status)" /></td>
                                    <td class="tabular-nums text-center">{{ r.attempts }}</td>
                                    <td class="text-sm text-surface-500 whitespace-nowrap">{{ r.createdAt | date: 'short' }}</td>
                                    <td class="text-sm text-surface-500 whitespace-nowrap">{{ r.notifiedAt ? (r.notifiedAt | date: 'short') : '—' }}</td>
                                    <td class="text-sm text-red-600 dark:text-red-400">{{ r.lastError || '—' }}</td>
                                </tr>
                            </ng-template>
                        </p-table>
                        <p-button label="Refresh" icon="pi pi-refresh" [text]="true" size="small" styleClass="mt-2" (onClick)="loadDispatches(true)" />
                    } @else {
                        <p class="text-sm text-surface-500">
                            @if (startedAtPointOfSale()) {
                                A sale taken at the store's own counter or kiosk — it is not sent anywhere.
                            } @else {
                                Nothing was sent for this order.
                                @if (canSendEdge()) { Use “Send to store” above to send it now. }
                            }
                        </p>
                    }

                    @if (dispatches()?.xtEvents?.length) {
                        <h6 class="mt-4">POS activity</h6>
                        <p-table [value]="dispatches()!.xtEvents" styleClass="p-datatable-sm">
                            <ng-template pTemplate="header">
                                <tr><th>When</th><th>What happened</th><th>Outcome</th><th>Note</th><th class="w-10"></th></tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-e let-i="rowIndex">
                                <tr>
                                    <td class="text-sm text-surface-500 whitespace-nowrap">{{ e.occurredAt | date: 'short' }}</td>
                                    <td class="text-sm"><span [title]="tooltip(e.type)">{{ label(e.type) }}</span></td>
                                    <td><p-tag [value]="label(e.status)" [pTooltip]="tooltip(e.status)" [severity]="dispatchSev(e.status)" /></td>
                                    <td class="text-sm text-surface-500">{{ e.comment || '—' }}</td>
                                    <td>
                                        @if (e.payload) {
                                            <p-button [icon]="expandedXtEvent() === i ? 'pi pi-chevron-up' : 'pi pi-code'"
                                                      [text]="true" size="small" (onClick)="toggleXtEvent(i)" />
                                        }
                                    </td>
                                </tr>
                                @if (expandedXtEvent() === i && e.payload) {
                                    <tr>
                                        <td colspan="5">
                                            <!-- The wire evidence this event registered: the literal request body for
                                                 the call, XT's raw response for succeeded/failed. -->
                                            <pre class="text-xs bg-surface-100 dark:bg-surface-800 rounded p-3 overflow-x-auto whitespace-pre-wrap max-h-96">{{ prettyPayload(e.payload) }}</pre>
                                        </td>
                                    </tr>
                                }
                            </ng-template>
                        </p-table>
                    }
                }

                @if (detailTab === 'status') {
                    @if (statusLoading()) {
                        <p class="text-sm text-surface-500">Loading status history…</p>
                    } @else if (statusHistory()?.entries?.length) {
                        <p-table [value]="statusHistory()!.entries" styleClass="p-datatable-sm">
                            <ng-template pTemplate="header">
                                <tr><th>When</th><th>Track</th><th>From</th><th>To</th><th>Reported by</th></tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-e>
                                <tr>
                                    <td class="text-sm text-surface-500 whitespace-nowrap">{{ e.at | date: 'short' }}</td>
                                    <td><p-tag [value]="label(e.track)" [pTooltip]="tooltip(e.track)" severity="secondary" /></td>
                                    <!-- Only the POS hand-off track speaks OUR vocabulary; the other two carry
                                         Magento's and the courier's own words and are shown as they were said. -->
                                    <td class="text-sm text-surface-500">{{ trackStatus(e.track, e.oldStatus) || '—' }}</td>
                                    <td class="text-sm"><p-tag [value]="trackStatus(e.track, e.newStatus)" [pTooltip]="trackStatusTooltip(e.track, e.newStatus)" [severity]="statusSev(e.newStatus)" /></td>
                                    <td class="text-sm">{{ label(e.reportedBy) || '—' }}</td>
                                </tr>
                            </ng-template>
                        </p-table>
                    } @else {
                        <p class="text-sm text-surface-500">No status changes recorded.</p>
                    }
                }
            }
        </p-dialog>

        <!-- Receipt straight from a list row — hosts the SAME shared component as the detail modal's
             Receipt tab, so there is exactly one paper rendering in the app. PrimeNG drops the dialog
             content from the DOM while hidden, so the component mounts (and fetches) per open. -->
        <p-dialog [(visible)]="showReceipt" [modal]="true" [style]="{ width: '26rem' }" [dismissableMask]="true"
                  [header]="'Receipt — ' + (receiptOrder()?.externalOrderId ?? '')">
            @if (receiptOrder(); as ro) {
                <app-order-receipt [locationId]="locationId()" [externalOrderId]="ro.externalOrderId" [source]="ro.source" />
            }
        </p-dialog>
    `
})
export class OrdersTab implements OnInit {
    private readonly api = inject(MiddlewareApi);
    private readonly perms = inject(PermissionsService);

    // Location comes from the parent (merchant-detail tabs). Signal input — read via this.locationId().
    readonly locationId = input.required<string>();
    // A search to open with (the Orders page's ?search= deep link). Read once, before the table's first load.
    readonly initialSearch = input<string | null>(null);

    readonly items = signal<LocationOrderListItem[]>([]);
    readonly total = signal(0);
    readonly first = signal(0);
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);
    readonly stats = signal<OrderLocationStats | null>(null);
    readonly pageSize = 25;

    search = '';
    status: string | null = null;
    source: string | null = null;
    deliveryStatus: string | null = null;

    // Filter vocabularies. Statuses are per-source tokens harvested from the loaded page (there is no
    // canonical enum); sources/deliveries likewise accumulate from what actually shows up.
    readonly statusOptions = signal<{ label: string; value: string }[]>([]);
    readonly sourceOptions = signal<{ label: string; value: string }[]>([]);
    readonly deliveryOptions = signal<{ label: string; value: string }[]>([]);

    readonly showDetail = signal(false);
    readonly selected = signal<LocationOrderListItem | null>(null);
    readonly detail = signal<LocationOrderDetail | null>(null);
    readonly detailLoading = signal(false);
    readonly detailError = signal<string | null>(null);
    detailTab: 'summary' | 'receipt' | 'timeline' | 'status' | 'events' | 'delivery' | 'dispatches' = 'summary';
    readonly tabs = [
        { label: 'Summary', value: 'summary' },
        { label: 'Receipt', value: 'receipt' },
        { label: 'Timeline', value: 'timeline' },
        { label: 'Status history', value: 'status' },
        { label: 'Events', value: 'events' },
        { label: 'Delivery', value: 'delivery' },
        // The tab the user reads as "did the till get this order?" — named for that, not for the
        // OutboundDispatch machinery behind it.
        { label: 'POS hand-off', value: 'dispatches' }
    ];

    readonly timeline = signal<OrderTimelineItem[]>([]);
    readonly timelineTotal = signal(0);
    readonly timelineLoading = signal(false);
    private timelineLoaded = false;

    readonly streamEvents = signal<OrderStreamEvent[]>([]);
    readonly streamEventsLoading = signal(false);
    readonly expandedStreamEvent = signal<number | null>(null);
    private streamEventsLoaded = false;

    readonly statusHistory = signal<OrderStatusHistory | null>(null);
    readonly statusLoading = signal(false);
    private statusLoaded = false;

    // The delivery view — provider-agnostic. Nothing here knows about DoorDash: the courier's name, its
    // updates and its raw bodies all arrive as data, so a second courier needs no code here.
    readonly delivery = signal<OrderDeliveryDetail | null>(null);
    readonly deliveryLoading = signal(false);
    private deliveryLoaded = false;
    readonly expandedDeliveryUpdate = signal<number | null>(null);
    readonly replaying = signal(false);
    readonly replayResult = signal<ReplayWebhooksResult | null>(null);
    readonly replayError = signal<string | null>(null);

    // Dispatch records (the OutboundDelivery rows) + the manual send. The send is gated on
    // recipients:manage — it queues a REAL registration in the POS, not a read.
    readonly dispatches = signal<OrderDispatches | null>(null);
    readonly dispatchesLoading = signal(false);
    private dispatchesLoaded = false;
    readonly confirmSendXt = signal(false);
    readonly sendingXt = signal(false);
    readonly sendXtError = signal<string | null>(null);
    readonly sendXtSuccess = signal<string | null>(null);

    private readonly canManageXt = computed(() => this.perms.has('recipients:manage'));

    // A sale the store took at its own counter: recorded only, so every lever that would send or print it is hidden
    // (the server refuses each of them too).
    readonly startedAtPointOfSale = computed(() =>
        POINT_OF_SALE_SOURCES.includes((this.selected()?.source ?? '').toLowerCase()));

    // The send-to-store lever: offered once the order has an Edge hand-off, which is also where its outcome shows.
    readonly confirmSendEdge = signal(false);
    readonly sendingEdge = signal(false);
    readonly sendEdgeError = signal<string | null>(null);
    readonly sendEdgeSuccess = signal<string | null>(null);
    // Offered wherever the store Edge receives the location's orders - which, with no recipient rows, is the default -
    // or the order already has an edge hand-off. It is also how an order that went to the legacy XT hand-off reaches the store.
    readonly canSendEdge = computed(() => {
        // Not for an order the legacy POS already has: it would be fulfilled twice (the server refuses it too).
        // xtRegistered(), not the bare id: the store's own POS reports its number into the same field now, and
        // reading that as "XT has it" would hide this lever from the very order the store is asking about.
        const d = this.dispatches();
        if (!this.canManageXt() || this.dispatchesLoading() || this.startedAtPointOfSale() || !d || this.xtRegistered()) return false;
        return (d.effectiveRecipients ?? []).some((r) => r.toLowerCase() === 'edge')
            || (d.dispatches ?? []).some((r) => (r.recipient ?? '').toLowerCase() === 'edge');
    });

    // XT provably has this order: it assigned its own order id (the chip renders it) AND the xt dispatch
    // row reached Notified. Both facts come from the dispatches read the tab already made. A failed or
    // still-pending dispatch leaves this false — the levers exist for exactly those states.
    readonly xtRegistered = computed(() => {
        const d = this.dispatches();
        if (!d?.xtOrderId) return false;
        return (d.dispatches ?? []).some((r) =>
            (r.recipient ?? '').toLowerCase() === 'xt' && (r.status ?? '').toLowerCase() === 'notified');
    });

    // A successful complete push is on record — the XT activity list renders that very row
    // (XtStatusUpdated / complete; a refusal is 'complete-failed' and does NOT count).
    readonly xtCompleteRecorded = computed(() =>
        (this.dispatches()?.xtEvents ?? []).some((e) =>
            e.type === 'XtStatusUpdated' && (e.status ?? '').toLowerCase() === 'complete'));

    // Hidden once XT has the order: on a healthy order the chip is the evidence, and the lever's only
    // remaining outcome would be a duplicate POS order (the server refuses that too — this just stops
    // offering it). Hidden while loading so the button doesn't flash before the facts arrive.
    // LEGACY: only while the XT POS hand-off is switched on (the server refuses it otherwise).
    readonly canSendXt = computed(() =>
        this.canManageXt() && !this.dispatchesLoading() && !this.xtRegistered() && !this.startedAtPointOfSale()
        && this.dispatches()?.legacyXtHandoffEnabled === true);

    // Marking the order complete in the POS. Same permission as the send — it changes XT, not us.
    // Only offered once the order HAS an XT id (without one there is no POS order to complete), and
    // hidden again once a successful complete is recorded — pressing it then would be a no-op the
    // server answers "already complete" to anyway.
    readonly confirmCompleteXt = signal(false);
    readonly completingXt = signal(false);
    readonly completeXtError = signal<string | null>(null);
    readonly completeXtSuccess = signal<string | null>(null);
    // xtRegistered(), not the bare id: this lever calls the LEGACY XT connector, and a number reported by the
    // store's own point of sale is not an order XT has ever heard of. Offering it there would push a status at
    // XT quoting an id that means nothing to it.
    readonly canCompleteXt = computed(() =>
        this.canManageXt() && !this.dispatchesLoading()
        && this.xtRegistered() && !this.xtCompleteRecorded());

    // ---- The store Edge's fulfilment push and cancel (edge-status / edge-cancel) -----------------------------
    // The store-side twins of the XT levers. "Update store" is offered once the order has a LIVE Edge hand-off
    // (pending, sent, notified or held): a retired one means no store will ever read the update, and the server
    // refuses that too. "Cancel at store" is offered for any Edge hand-off, whatever its state - the store remembers
    // a cancellation for an order it has not seen. Same permission as the other POS levers.
    readonly edgeHandoffLive = computed(() =>
        (this.dispatches()?.dispatches ?? []).some((r) =>
            (r.recipient ?? '').toLowerCase() === 'edge'
            && !['abandoned', 'deadlettered'].includes((r.status ?? '').toLowerCase())));
    readonly edgeHandoffAny = computed(() =>
        (this.dispatches()?.dispatches ?? []).some((r) => (r.recipient ?? '').toLowerCase() === 'edge'));
    // A successful completed push to the store is on record (StatusSentToStore / completed; a refusal is
    // 'completed-failed' and does NOT count). The tag renders it, and the lever stops offering 'complete'.
    readonly storeCompleteRecorded = computed(() =>
        (this.dispatches()?.xtEvents ?? []).some((e) =>
            e.type === 'StatusSentToStore' && (e.status ?? '').toLowerCase() === 'completed'));
    readonly canUpdateStore = computed(() =>
        this.canManageXt() && !this.dispatchesLoading() && !this.startedAtPointOfSale() && this.edgeHandoffLive());
    readonly canCancelAtStore = computed(() =>
        this.canManageXt() && !this.dispatchesLoading() && !this.startedAtPointOfSale() && this.edgeHandoffAny());
    readonly showStoreUpdate = signal(false);
    readonly updatingStore = signal(false);
    readonly storeUpdateError = signal<string | null>(null);
    readonly storeUpdateSuccess = signal<string | null>(null);
    storeStatus: 'ready' | 'out' | 'shipped' | 'complete' = 'complete';
    storeCarrier = '';
    storeTrackingNumber = '';
    storeTrackingUrl = '';
    storeNote = '';
    readonly storeStatusOptions = computed(() => [
        { label: 'Ready for pickup', value: 'ready' },
        { label: 'Out for delivery', value: 'out' },
        { label: 'Shipped', value: 'shipped' },
        // Hidden once a completed push is on record: the server would answer "already sent" anyway.
        ...(this.storeCompleteRecorded() ? [] : [{ label: 'Complete', value: 'complete' }])
    ]);
    readonly confirmCancelStore = signal(false);
    readonly cancellingStore = signal(false);
    readonly storeCancelError = signal<string | null>(null);
    readonly storeCancelSuccess = signal<string | null>(null);
    storeCancelReason = '';

    // The live print record (ticket / fetched / location gate) and the force re-print lever. Same
    // permission stance as the other store-changing levers: the press makes a till-side device print.
    // printState stays null when the endpoint can't answer (older API, no permission) — the Printed row
    // then falls back to the lifecycle-only display rather than guessing.
    readonly printState = signal<OrderPrintState | null>(null);
    readonly confirmReprint = signal(false);
    readonly reprinting = signal(false);
    readonly reprintError = signal<string | null>(null);
    readonly reprintResult = signal<ReprintOrderResult | null>(null);
    readonly canReprint = computed(() => this.perms.has('recipients:manage'));

    // The per-row receipt dialog. Rendering belongs to the shared OrderReceiptView (self-loading, so
    // nothing to fetch here) — this only remembers WHICH row was asked for.
    readonly showReceipt = signal(false);
    readonly receiptOrder = signal<LocationOrderListItem | null>(null);

    private statsLoadedFor: string | null = null;

    constructor() {
        // Load the stats row once the input resolves (and reload if the parent swaps location).
        effect(() => {
            const loc = this.locationId();
            if (loc && loc !== this.statsLoadedFor) {
                this.statsLoadedFor = loc;
                this.stats.set(null);
                this.api.getLocationOrderStats(loc).subscribe({
                    next: (s) => this.stats.set(s),
                    error: () => this.stats.set(null)
                });
            }
        });
    }

    ngOnInit(): void {
        const s = this.initialSearch();
        if (s) this.search = s;
    }

    onPage(e: { first?: number | null; rows?: number | null }): void {
        const first = e.first ?? 0;
        this.first.set(first);
        this.load(Math.floor(first / this.pageSize) + 1);
    }

    reload(): void {
        this.first.set(0);
        this.load(1);
    }

    private load(page: number): void {
        const loc = this.locationId();
        if (!loc) return;
        this.loading.set(true);
        this.error.set(null);
        this.api.getLocationOrders(loc, {
            page,
            pageSize: this.pageSize,
            search: this.search || undefined,
            status: this.status || undefined,
            source: this.source || undefined,
            deliveryStatus: this.deliveryStatus || undefined
        }).subscribe({
            next: (r) => {
                const rows = r.items ?? [];
                this.items.set(rows);
                this.total.set(r.total ?? 0);
                this.harvestFilters(rows);
                this.loading.set(false);
            },
            error: (e) => {
                this.error.set(this.describe(e));
                this.items.set([]);
                this.total.set(0);
                this.loading.set(false);
            }
        });
    }

    // Grow the filter dropdowns from tokens actually seen (statuses/sources/deliveries are per-source,
    // not a shared enum). deliveryStatus is '' when absent — skip those.
    private harvestFilters(rows: LocationOrderListItem[]): void {
        this.mergeOptions(this.statusOptions, rows.map((r) => r.status));
        // Sources are our own vocabulary, so the dropdown shows labels; the VALUE stays the raw token
        // because that is what the query string carries.
        this.mergeOptions(this.sourceOptions, rows.map((r) => r.source), true);
        this.mergeOptions(this.deliveryOptions, rows.map((r) => r.deliveryStatus).filter((v) => !!v));
    }

    private mergeOptions(sig: typeof this.statusOptions, values: string[], relabel = false): void {
        const have = new Set(sig().map((o) => o.value));
        let changed = false;
        for (const v of values) {
            if (v && !have.has(v)) { have.add(v); changed = true; }
        }
        if (changed) {
            sig.set(relabel
                ? orderTokenOptions(have)
                : [...have].sort().map((v) => ({ label: v, value: v })));
        }
    }

    open(o: LocationOrderListItem): void {
        this.selected.set(o);
        this.showDetail.set(true);
        this.detailTab = 'summary';
        this.detail.set(null);
        this.detailError.set(null);
        this.detailLoading.set(true);
        this.timeline.set([]);
        this.timelineTotal.set(0);
        this.timelineLoaded = false;
        this.statusHistory.set(null);
        this.statusLoaded = false;
        this.streamEvents.set([]);
        this.streamEventsLoaded = false;
        this.expandedStreamEvent.set(null);
        this.dispatches.set(null);
        this.dispatchesLoaded = false;
        this.delivery.set(null);
        this.deliveryLoaded = false;
        this.expandedDeliveryUpdate.set(null);
        this.replayResult.set(null);
        this.replayError.set(null);
        this.confirmSendXt.set(false);
        this.sendXtError.set(null);
        this.sendXtSuccess.set(null);
        this.confirmSendEdge.set(false);
        this.sendEdgeError.set(null);
        this.sendEdgeSuccess.set(null);
        this.confirmCompleteXt.set(false);
        this.completeXtError.set(null);
        this.completeXtSuccess.set(null);
        this.expandedXtEvent.set(null);
        this.printState.set(null);
        this.confirmReprint.set(false);
        this.reprintError.set(null);
        this.reprintResult.set(null);

        const loc = this.locationId();
        // Always pass source: an id can be shared across sources at a location, and getLocationOrder
        // WITHOUT source then returns HTTP 400 (surfaced via describe). We know the source from the row.
        this.api.getLocationOrder(loc, o.externalOrderId, o.source).subscribe({
            next: (d) => { this.detail.set(d); this.detailLoading.set(false); },
            error: (e) => { this.detailError.set(this.describe(e)); this.detailLoading.set(false); }
        });
        this.loadPrintState();
    }

    // Timeline and status-history are lazy per tab — fetched the first time each tab is opened.
    // detailTab is already updated by two-way ngModel before onChange fires, so we read it directly.
    // (Receipt needs no line here: its component mounts with the tab and loads itself.)
    onTabChange(): void {
        if (this.detailTab === 'timeline' && !this.timelineLoaded) this.loadTimeline();
        if (this.detailTab === 'status' && !this.statusLoaded) this.loadStatusHistory();
        if (this.detailTab === 'events' && !this.streamEventsLoaded) this.loadStreamEvents();
        if (this.detailTab === 'delivery' && !this.deliveryLoaded) this.loadDelivery();
        if (this.detailTab === 'dispatches' && !this.dispatchesLoaded) this.loadDispatches();
    }

    readonly expandedXtEvent = signal<number | null>(null);

    toggleXtEvent(i: number): void {
        this.expandedXtEvent.set(this.expandedXtEvent() === i ? null : i);
    }

    /** The rows of the Refunds table: one per line a refund gave back, read off its items digest
     *  ('SKU:quantity:amount:kind:name' joined by ';' — the last two absent on refunds recorded before they
     *  existed, which then read by SKU), then the refund's total when it is more than one line. A refund with
     *  no lines at all is one row: the whole order when it says it is full, an amount otherwise.
     *
     *  The lines are what the refund NAMED; its amount is what was credited and went back. They can disagree -
     *  a register's refund Magento credited differently, or the order's reversal after part of it had already
     *  been returned - and then the closing row says what was returned and what the lines add up to. */
    /** What the line cost the customer: the goods less the discount plus the tax - Magento's own admin "Row Total"
     *  (row_total - discount + tax) and what a refund of the line gives back. `lineTotal` is Magento's row_total, the
     *  goods alone (unit x qty, before discount and tax); `discount` and `taxAmount` are the LINE's, not a unit's.
     *  A line stored without a total (an older document) is read as unit x qty, as the refund code reads it. */
    rowTotal(line: OrderLineState): number {
        const goods = line.lineTotal > 0 ? line.lineTotal : line.unitPrice * line.quantity;
        return goods - (line.discount ?? 0) + (line.taxAmount ?? 0);
    }

    refundRows(refunds: { reason: string; status: string; amount: number; isFullRefund: boolean; itemsDigest?: string | null; refundedAt?: string | null }[]): RefundRow[] {
        const rows: RefundRow[] = [];
        for (const r of refunds) {
            const lines = (r.itemsDigest ?? '')
                .split(';')
                .map((token) => token.split(':'))
                .filter((parts) => (parts[0] ?? '').trim().length > 0)
                .map((parts) => {
                    const kind: 'fee' | 'product' = (parts[3] ?? '').trim() === 'fee' ? 'fee' : 'product';
                    const name = (parts[4] ?? '').trim() || parts[0].trim();
                    const qty = Number(parts[1] ?? '');
                    return { item: name, qty: kind === 'fee' || !Number.isFinite(qty) ? null : qty, amount: Number(parts[2] ?? '') || 0, kind };
                });

            const head = { refundedAt: r.refundedAt ?? null, reason: r.reason, status: r.status };
            if (!lines.length) {
                rows.push({ ...head, first: true, item: r.isFullRefund ? 'Whole order' : 'Amount only', qty: null, amount: r.amount, kind: 'fee', isTotal: false, differs: false, nothingReturned: false, note: null });
                continue;
            }

            const linesTotal = lines.reduce((sum, line) => sum + line.amount, 0);
            const differs = Math.abs(linesTotal - r.amount) >= 0.005;
            // Only a refund that claims to be done: one still 'credited' has not returned its money YET, and says so.
            const nothingReturned = differs && Math.abs(r.amount) < 0.005 && r.status === 'refunded';

            lines.forEach((line, i) => rows.push({ ...head, first: i === 0, ...line, isTotal: false, differs, nothingReturned, note: null }));

            if (lines.length > 1 || differs) {
                rows.push({
                    ...head, first: false, qty: null, amount: r.amount, kind: 'total', isTotal: true, differs, nothingReturned,
                    item: differs ? 'Returned' : 'Total refunded',
                    note: differs ? `the lines above add up to ${linesTotal.toFixed(2)}` : null
                });
            }
        }
        return rows;
    }

    /** Pretty-print a stored wire body when it is JSON; anything else renders verbatim. */
    prettyPayload(payload: string): string {
        try {
            return JSON.stringify(JSON.parse(payload), null, 2);
        } catch {
            return payload;
        }
    }

    loadDelivery(force = false): void {
        const o = this.selected();
        if (!o || (this.deliveryLoaded && !force)) return;
        this.deliveryLoaded = true;
        this.deliveryLoading.set(true);
        this.api.getOrderDelivery(this.locationId(), o.externalOrderId, o.source).subscribe({
            next: (r) => { this.delivery.set(r); this.deliveryLoading.set(false); },
            error: (e) => {
                this.delivery.set(null);
                this.deliveryLoading.set(false);
                this.replayError.set(this.describe(e));
            }
        });
    }

    toggleDeliveryUpdate(i: number): void {
        this.expandedDeliveryUpdate.set(this.expandedDeliveryUpdate() === i ? null : i);
    }

    /** Re-runs the courier's webhooks for this order, then reloads so the result is visible, not claimed. */
    replayDelivery(): void {
        const o = this.selected();
        if (!o) return;
        this.replaying.set(true);
        this.replayError.set(null);
        this.replayResult.set(null);
        this.api.replayOrderDelivery(this.locationId(), o.externalOrderId, o.source).subscribe({
            next: (r) => {
                this.replaying.set(false);
                this.replayResult.set(r);
                this.loadDelivery(true);
            },
            error: (e) => {
                this.replaying.set(false);
                this.replayError.set(this.describe(e));
            }
        });
    }

    /** Severity for a courier's own status word. Cancellations and failures first, as everywhere else. */
    deliveryUpdateSev(s: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const v = (s ?? '').toLowerCase();
        if (/(cancel|fail|return|undeliverable)/.test(v)) return 'danger';
        if (/(dropped_off|delivered|complete)/.test(v)) return 'success';
        if (/(delay|hold|reassign)/.test(v)) return 'warn';
        if (/(picked_up|confirmed|enroute|arriv)/.test(v)) return 'info';
        return 'secondary';
    }

    loadDispatches(force = false): void {
        const o = this.selected();
        if (!o || (this.dispatchesLoaded && !force)) return;
        this.dispatchesLoaded = true;
        this.dispatchesLoading.set(true);
        this.api.getOrderDispatches(this.locationId(), o.externalOrderId, o.source).subscribe({
            next: (r) => { this.dispatches.set(r); this.dispatchesLoading.set(false); },
            error: (e) => {
                this.dispatches.set(null);
                this.dispatchesLoading.set(false);
                this.sendXtError.set(this.describe(e));
            }
        });
    }

    sendToXt(): void {
        const o = this.selected();
        if (!o) return;
        this.sendingXt.set(true);
        this.sendXtError.set(null);
        this.sendXtSuccess.set(null);
        this.api.sendOrderToXt(this.locationId(), o.externalOrderId, o.source).subscribe({
            next: (r) => {
                this.sendingXt.set(false);
                this.confirmSendXt.set(false);
                // Converged, not queued: the server saw XT already has the order and sent nothing.
                this.sendXtSuccess.set(r.alreadyRegistered
                    ? (r.message ?? `Already in XT as ${r.xtOrderId} — nothing was sent.`)
                    : 'Send queued — a new record appears below; refresh to follow its progress.');
                this.loadDispatches(true);
            },
            error: (e) => {
                this.sendingXt.set(false);
                this.confirmSendXt.set(false);
                this.sendXtError.set(this.describe(e));
            }
        });
    }

    /** Sends the order to the location's store Edge again, then reloads so the re-queued hand-off is visible. */
    sendToEdge(): void {
        const o = this.selected();
        if (!o) return;
        this.sendingEdge.set(true);
        this.sendEdgeError.set(null);
        this.sendEdgeSuccess.set(null);
        this.api.sendOrderToEdge(this.locationId(), o.externalOrderId, o.source).subscribe({
            next: (r) => {
                this.sendingEdge.set(false);
                this.confirmSendEdge.set(false);
                this.sendEdgeSuccess.set(r.message);
                this.loadDispatches(true);
            },
            error: (e) => {
                this.sendingEdge.set(false);
                this.confirmSendEdge.set(false);
                this.sendEdgeError.set(this.describe(e));
            }
        });
    }

    /** Pushes "complete" to the POS for this order, then reloads so the recorded attempt is visible. */
    completeInXt(): void {
        const o = this.selected();
        if (!o) return;
        this.completingXt.set(true);
        this.completeXtError.set(null);
        this.completeXtSuccess.set(null);
        this.api.setOrderXtStatus(this.locationId(), o.externalOrderId, 'complete', o.source).subscribe({
            next: (r) => {
                this.completingXt.set(false);
                this.confirmCompleteXt.set(false);
                // alreadyApplied = the success was on record and XT was not called again.
                this.completeXtSuccess.set(r.alreadyApplied
                    ? 'Already complete in XT — the earlier success is on record, so nothing was sent.'
                    : 'XT accepted the completion — the attempt is recorded in XT activity below.');
                this.loadDispatches(true);
            },
            error: (e) => {
                this.completingXt.set(false);
                this.confirmCompleteXt.set(false);
                this.completeXtError.set(this.describe(e));
                // The refusal is recorded on the order too, so refresh either way.
                this.loadDispatches(true);
            }
        });
    }

    /** Opens the store update form. Defaults to 'complete' - the ask this lever exists for - unless the store has
     *  already been told, in which case the next likely ask is a shipment. */
    openStoreUpdate(): void {
        this.storeStatus = this.storeCompleteRecorded() ? 'shipped' : 'complete';
        this.storeUpdateError.set(null);
        this.storeUpdateSuccess.set(null);
        this.showStoreUpdate.set(true);
    }

    /** Tells the store Edge where the order got to, then reloads so the recorded send is visible. */
    updateStore(): void {
        const o = this.selected();
        if (!o) return;
        this.updatingStore.set(true);
        this.storeUpdateError.set(null);
        this.storeUpdateSuccess.set(null);
        const blank = (v: string) => (v.trim() ? v.trim() : null);
        this.api.setOrderEdgeStatus(this.locationId(), o.externalOrderId, {
            status: this.storeStatus,
            carrier: blank(this.storeCarrier),
            trackingNumber: blank(this.storeTrackingNumber),
            trackingUrl: blank(this.storeTrackingUrl),
            note: blank(this.storeNote)
        }, o.source).subscribe({
            next: (r) => {
                this.updatingStore.set(false);
                this.showStoreUpdate.set(false);
                this.storeUpdateSuccess.set(r.message);
                this.loadDispatches(true);
            },
            error: (e) => {
                this.updatingStore.set(false);
                this.storeUpdateError.set(this.describe(e));
                // A refused send is recorded on the order too, so refresh either way.
                this.loadDispatches(true);
            }
        });
    }

    /** Tells the store Edge to stop working on the order (the store only), then reloads. */
    cancelAtStore(): void {
        const o = this.selected();
        if (!o) return;
        this.cancellingStore.set(true);
        this.storeCancelError.set(null);
        this.storeCancelSuccess.set(null);
        this.api.cancelOrderAtEdge(this.locationId(), o.externalOrderId, this.storeCancelReason.trim() || null, o.source).subscribe({
            next: (r) => {
                this.cancellingStore.set(false);
                this.confirmCancelStore.set(false);
                this.storeCancelReason = '';
                this.storeCancelSuccess.set(r.message);
                this.loadDispatches(true);
            },
            error: (e) => {
                this.cancellingStore.set(false);
                this.confirmCancelStore.set(false);
                this.storeCancelError.set(this.describe(e));
            }
        });
    }

    /** The live print record. Failure leaves printState null — the Printed row then shows only the
     *  lifecycle facts, which is honest: we could not read the record, so we do not describe it. */
    loadPrintState(): void {
        const o = this.selected();
        if (!o) return;
        this.api.getOrderPrintState(this.locationId(), o.externalOrderId, o.source).subscribe({
            next: (ps) => this.printState.set(ps),
            error: () => this.printState.set(null)
        });
    }

    /** Queues the order for the store's printer again, then re-reads the record so the "queued for
     *  printer" state shown is the stored one, not a claim. */
    reprint(): void {
        const o = this.selected();
        if (!o) return;
        this.reprinting.set(true);
        this.reprintError.set(null);
        this.reprintResult.set(null);
        this.api.reprintOrder(this.locationId(), o.externalOrderId, o.source).subscribe({
            next: (r) => {
                this.reprinting.set(false);
                this.confirmReprint.set(false);
                this.reprintResult.set(r);
                this.loadPrintState();
            },
            error: (e) => {
                this.reprinting.set(false);
                this.confirmReprint.set(false);
                this.reprintError.set(this.describe(e));
            }
        });
    }

    /** Receipt from a list row, without the detail modal. The event stops here: the row's own click
     *  would otherwise ALSO fire and open the detail over the receipt. */
    openReceipt(o: LocationOrderListItem, e?: Event): void {
        e?.stopPropagation();
        this.receiptOrder.set(o);
        this.showReceipt.set(true);
    }

    private loadStreamEvents(): void {
        const o = this.selected();
        if (!o) return;
        this.streamEventsLoaded = true;
        this.streamEventsLoading.set(true);
        this.api.getLocationOrderEvents(this.locationId(), o.externalOrderId, o.source || 'magento').subscribe({
            next: (r) => { this.streamEvents.set(r ?? []); this.streamEventsLoading.set(false); },
            error: () => { this.streamEvents.set([]); this.streamEventsLoading.set(false); }
        });
    }

    toggleStreamEvent(i: number): void {
        this.expandedStreamEvent.set(this.expandedStreamEvent() === i ? null : i);
    }

    /** The row's label: the fact's own token where the alias is a generic envelope ('CreditMemoIssued' under
     *  orders_operation_recorded_v1 -> 'Credit memo issued'), else the stored alias made readable
     *  ('orders_dispatch_state_changed_v1' -> 'POS hand-off changed'). The alias itself stays on the tooltip —
     *  it is the string the store rehydrates by. */
    streamEventLabel(ev: OrderStreamEvent): string {
        return ev.fact ? orderLabel(ev.fact) : orderEventAliasLabel(ev.eventType);
    }

    streamEventTooltip(ev: OrderStreamEvent): string {
        return [ev.fact ? orderTooltip(ev.fact) : '', `alias: ${ev.eventType}`].filter((s) => !!s).join(' · ');
    }

    prettyStreamEvent(ev: OrderStreamEvent): string {
        try { return JSON.stringify(JSON.parse(ev.data), null, 2); } catch { return ev.data; }
    }

    private loadTimeline(): void {
        const o = this.selected();
        if (!o) return;
        this.timelineLoaded = true;
        this.timelineLoading.set(true);
        this.api.getLocationOrderTimeline(this.locationId(), o.externalOrderId, { source: o.source, page: 1, pageSize: 100 }).subscribe({
            next: (r) => { this.timeline.set(r.items ?? []); this.timelineTotal.set(r.total ?? 0); this.timelineLoading.set(false); },
            error: () => { this.timeline.set([]); this.timelineTotal.set(0); this.timelineLoading.set(false); }
        });
    }

    private loadStatusHistory(): void {
        const o = this.selected();
        if (!o) return;
        this.statusLoaded = true;
        this.statusLoading.set(true);
        this.api.getLocationOrderStatusHistory(this.locationId(), o.externalOrderId, { source: o.source }).subscribe({
            next: (h) => { this.statusHistory.set(h); this.statusLoading.set(false); },
            error: () => { this.statusHistory.set(null); this.statusLoading.set(false); }
        });
    }

    // Prefer the decimal value for Totals rows (oldDecimal/newDecimal), fall back to the text value.
    // A row whose changeType is one of OUR tracks carries one of OUR tokens in From/To, so it is
    // relabelled; every other row holds the source system's or the courier's own word and is left alone.
    display(t: OrderTimelineItem, which: 'old' | 'new'): string {
        if (which === 'old') {
            if (t.oldDecimal != null) return String(t.oldDecimal);
            return this.ourToken(t.changeType) ? orderLabel(t.oldValue) || '—' : t.oldValue ?? '—';
        }
        if (t.newDecimal != null) return String(t.newDecimal);
        return this.ourToken(t.changeType) ? orderLabel(t.newValue) || '—' : t.newValue ?? '—';
    }

    // --- The ONE order vocabulary, read from core/order-vocabulary --------------------------------
    // Every token this screen renders goes through these two, so there is one dictionary for the whole
    // app and no column keeps a private copy. Nothing here is stored, sent, hashed or matched against:
    // the tokens are idempotency keys, Marten aliases and published lifecycle statuses, and renaming one
    // would be a data migration. The label is for the operator; the tooltip keeps the raw token for us.

    /** Token -> operator English. Unknown tokens are humanised, never blanked. */
    label(token: string | null | undefined): string {
        return orderLabel(token);
    }

    /** The label's meaning where it has one, plus the raw token — always. */
    tooltip(token: string | null | undefined): string {
        return orderTooltip(token);
    }

    /** Timeline/status-history rows whose status values belong to OUR vocabulary rather than to Magento's
     *  or the courier's. Dispatch is the send-to-POS machine; Fulfillment is our own stage set; an Operation
     *  is named by one of our own event types or operations ('CreditMemoIssued', 'RefundFailed',
     *  'XtStatusUpdated'), which read as stored tokens until they go through the dictionary. */
    private ourToken(changeType: string | null | undefined): boolean {
        const v = (changeType ?? '').toLowerCase();
        return v === 'dispatch' || v === 'fulfillment' || v === 'operation';
    }

    /** A status-history status, relabelled only on the track we own. The Order track carries the source
     *  system's own word and the Delivery track carries the courier's — translating either from our
     *  dictionary would be us guessing at another system's meaning. */
    trackStatus(track: string | null | undefined, status: string | null | undefined): string {
        return (track ?? '').toLowerCase() === 'dispatch' ? orderLabel(status) : status ?? '';
    }

    trackStatusTooltip(track: string | null | undefined, status: string | null | undefined): string {
        return (track ?? '').toLowerCase() === 'dispatch' ? orderTooltip(status) : '';
    }

    // --- Shipping CODE -> human LABEL -------------------------------------------------------------
    // The order book stores the source system's own codes ('doordashshipping_delivery',
    // 'doordashshipping') because codes are what XT, Magento and the courier match on. They are also
    // unreadable to the human reading this dialog, so these render a label NEXT TO the code, never
    // instead of it. Nothing here is stored, hashed, sent or matched against — it is view-layer
    // formatting of a value the API already returned, which is why it can live in the client at all.
    //
    // The display names are taken from the Magento module source rather than invented:
    //   carrier 'doordashshipping' -> 'DoorDash'
    //       (Eleva/DoorDash/etc/config.xml, <carriers><doordashshipping><title>DoorDash</title>)
    //   method  'delivery' -> 'Delivery', 'pickup' -> 'In-Store Pickup'
    //       (Eleva/CustomCheckout/Plugin/Sales/Order/ShippingDescriptionPlugin.php, which rewrites exactly
    //        these two codes to exactly these two strings for admin display)
    // An unrecognised code falls back to mechanical de-slugging, so it degrades to a tidier version of
    // ITSELF rather than to a confident wrong name.
    //
    // DELIBERATELY A DISPLAY-SIDE DERIVATION, NOT A STORED FIELD. Magento's own shipping_description is
    // the authoritative label, and it is not carried on the wire today; the moment the connector carries
    // it, read it here in preference to deriving. A derived label must never outrank one the host sent,
    // and it must never be written into the canonical state, where nothing downstream could tell the two
    // apart.
    private static readonly CARRIER_LABELS: Record<string, string> = {
        doordashshipping: 'DoorDash',
        flatrate: 'Flat Rate',
        freeshipping: 'Free Shipping',
        tablerate: 'Table Rate',
        ups: 'UPS',
        usps: 'USPS',
        fedex: 'FedEx',
        dhl: 'DHL'
    };

    private static readonly METHOD_LABELS: Record<string, string> = {
        delivery: 'Delivery',
        pickup: 'In-Store Pickup'
    };

    // 'doordashshipping_delivery' -> 'DoorDash - Delivery'. Empty in, empty out: the caller keeps its own
    // rendering for "the source told us nothing", which is a different statement from "we can't read it".
    // ---- The money on top of the goods, named. ------------------------------------------------------------------
    // Delivery is Magento's shipping amount PLUS any fee row the merchant's fee management maps to the delivery fee
    // (that row is how a merchant charges delivery when the shipping amount stays zero); the other rows keep their
    // own label with their XT bucket as a hint; whatever the fee total holds beyond the named rows is shown as
    // "not itemised", so a channel that sends only a total still adds up.
    deliveryFeeLines(s: OrderState): OrderFeeLine[] {
        return (s.fees ?? []).filter(f => f.mapping === 'deliveryFee');
    }

    otherFees(s: OrderState): OrderFeeLine[] {
        return (s.fees ?? []).filter(f => f.mapping !== 'deliveryFee');
    }

    deliveryTotal(s: OrderState): number {
        return (s.shippingTotal ?? 0) + this.deliveryFeeLines(s).reduce((sum, f) => sum + f.amount, 0);
    }

    /** What the delivery charge is made of, when it is made of more than one thing or comes from a merchant fee row. */
    deliveryHint(s: OrderState): string {
        const rows = this.deliveryFeeLines(s);
        const parts: string[] = [];
        if (s.shippingTotal) {
            const via = this.carrierLabel(s.carrier) || this.shippingLabel(s.shippingMethod) || 'shipping';
            parts.push(`${via} ${s.shippingTotal.toFixed(2)}`);
        }
        for (const f of rows) parts.push(`${f.label || f.code || 'fee'} ${f.amount.toFixed(2)}`);
        if (parts.length > 1) return parts.join(' + ');
        if (rows.length === 1 && !s.shippingTotal) return `${rows[0].label || rows[0].code} (merchant fee)`;
        return '';
    }

    feeKind(f: OrderFeeLine): string {
        switch (f.mapping) {
            case 'serviceFee': return 'service fee';
            case 'convenienceFee': return 'convenience fee';
            case '': case null: case undefined: return 'fee';
            default: return f.mapping;
        }
    }

    unnamedFees(s: OrderState): number {
        const named = (s.fees ?? []).reduce((sum, f) => sum + f.amount, 0);
        const rest = (s.feeTotal ?? 0) - named;
        return rest > 0.005 ? rest : 0;
    }

    shippingLabel(code: string | null | undefined): string {
        const raw = (code ?? '').trim();
        if (!raw) return '';

        // Split on the FIRST underscore only: Magento composes shipping_method as carrier_code + '_' +
        // method_code, and a method code may itself contain underscores.
        const sep = raw.indexOf('_');
        const carrier = this.carrierLabel(sep < 0 ? raw : raw.slice(0, sep));
        const method = sep < 0 ? '' : raw.slice(sep + 1);
        if (!method) return carrier;

        const methodLabel = OrdersTab.METHOD_LABELS[method.toLowerCase()] ?? this.humanize(method);

        // Carriers with a single method repeat themselves ('freeshipping_freeshipping'), which would
        // otherwise render as "Free Shipping - Free Shipping".
        return methodLabel.toLowerCase() === carrier.toLowerCase() ? carrier : `${carrier} - ${methodLabel}`;
    }

    carrierLabel(code: string | null | undefined): string {
        const raw = (code ?? '').trim();
        if (!raw) return '';
        return OrdersTab.CARRIER_LABELS[raw.toLowerCase()] ?? this.humanize(raw);
    }

    // Show the raw code alongside the label only when the label actually differs from it — otherwise the
    // same token would appear twice on the row for every source that already sends readable values.
    showCode(code: string | null | undefined, label: string): boolean {
        const raw = (code ?? '').trim();
        return raw.length > 0 && raw !== label;
    }

    private humanize(token: string): string {
        return token
            .split(/[_\-\s]+/)
            .filter((part) => part.length > 0)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
    }

    // --- Honest absences ---------------------------------------------------------------------------
    // Each of these answers "why is this empty", because the answer differs per field and only one of
    // them is ever a defect. Keeping the sentences here (rather than inline) keeps the template readable
    // and keeps the reasoning next to the evidence for it.

    // dispatchRecipient is written only by PointOfSaleHandoffStateChanged, and OrderDispatcher only emits one
    // per ENABLED OrderRecipients row at the location — so 'Pending' with no recipient is a configuration
    // answer ("nobody is configured to receive this merchant's orders"), not a lost value.
    recipientAbsence(d: LocationOrderDetail): string {
        return d.lifecycle.dispatchStatus === 'Pending'
            ? 'nothing sent yet — the location may have no enabled recipients'
            : 'not reported';
    }

    // lifecycle.deliveryStatus moves ONLY on a courier webhook (DeliveryStatusChanged). The snapshot's own
    // copy of the courier status is excluded from the order book by design, so an order whose webhooks
    // have not landed shows nothing here even when the source system knows a status.
    deliveryAbsence(d: LocationOrderDetail): string {
        return this.isPickup(d)
            ? 'not applicable — pickup order'
            : 'no courier update received yet';
    }

    // lifecycle.xtOrderId is written only by a PointOfSaleOrderNumberAssigned fact, and TWO points of sale can
    // now report one: the legacy XT middleware, and the store Edge, which writes the order into the merchant's
    // own POS and reports the number it gets back.
    //
    // STATES THE FACT, NOT A FORECAST. "not applicable" was wrong because an Edge CAN now report a number, but
    // "not yet" is wrong far more often: the export is a per-Edge opt-in that most merchants do not run at all,
    // its default mode rehearses the write and rolls it back (reporting nothing, ever), and an export that
    // exhausted its retries is abandoned. Central cannot see which of those a location is in — it learns only
    // that a number arrived — so promising one that is never coming sends an operator back to this screen.
    posOrderNumberAbsence(d: LocationOrderDetail): string {
        const recipient = (d.lifecycle.dispatchRecipient ?? '').toLowerCase();
        return recipient && recipient !== 'xt'
            ? 'none reported — the store has not sent a number from its point of sale'
            : 'not assigned — the legacy POS (XT) has not accepted this order (see the hand-off tab)';
    }

    /** The newest shipment WE recorded — the one the courier accept wrote. Read only as a fallback for the
     *  snapshot-owned courier fields above, so provenance stays honest: the state fields remain the source's,
     *  and these remain ours. */
    private ourShipment(d: LocationOrderDetail): any | null {
        const shipments = (d.state as any)?.shipments as any[] | undefined;
        if (!shipments || shipments.length === 0) return null;
        return shipments[shipments.length - 1];
    }

    ourDeliveryRef(d: LocationOrderDetail): string | null {
        return this.ourShipment(d)?.reference ?? null;
    }

    ourTrackingUrl(d: LocationOrderDetail): string | null {
        return this.ourShipment(d)?.trackingUrl ?? null;
    }

    ourTrackingNumber(d: LocationOrderDetail): string | null {
        return this.ourShipment(d)?.trackingNumber ?? null;
    }

    // A shipment is recorded from the snapshot only when the courier delivery reference is present, or
    // from XT when it reports a hand-off. Say which, so "no tracking number" is diagnosable from here.
    shipmentsAbsence(d: LocationOrderDetail): string {
        return this.isPickup(d)
            ? 'No shipments — this is a pickup order.'
            : 'No shipment recorded yet. One is recorded when the courier delivery reaches us (it carries the delivery reference) or when XT reports a hand-off.';
    }

    // Same distinction for the snapshot-owned courier references: absent means the SOURCE had not attached
    // a delivery when we read the order — the courier creates it after the order is placed.
    deliveryRefAbsence(d: LocationOrderDetail): string {
        return this.isPickup(d)
            ? 'not applicable — pickup order'
            : 'not provided by the source yet';
    }

    // fulfillmentType is the source's own lower-cased token, so match on substring rather than equality.
    private isPickup(d: LocationOrderDetail): boolean {
        return (d.state.fulfillmentType ?? '').includes('pickup');
    }

    // --- Severity heuristics. Status is the source system's own lower-cased token, so we key on
    // substrings rather than a fixed enum map (a canonical map would mislabel unknown source tokens).
    /** Statuses that mean "no money yet" in the source system, and are therefore the ones the ledger can
     *  contradict. Kept narrow on purpose: payment_review is a fraud hold and holding_for_stock is not about
     *  money at all, so neither is relabelled by a capture. */
    private readonly AWAITING_PAYMENT = ['pending_payment', 'pending'];

    /** The source's status, or what it MEANS once the ledger says the money landed. */
    statusLabel(o: LocationOrderListItem): string {
        return this.awaitingConfirmation(o) ? 'pending payment confirmation' : o.status;
    }

    /** Not a warning any more once the money is in — it is a step that has not happened yet. */
    rowStatusSev(o: LocationOrderListItem): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        return this.awaitingConfirmation(o) ? 'info' : this.statusSev(o.status);
    }

    statusHint(o: LocationOrderListItem): string {
        return this.awaitingConfirmation(o)
            ? 'The card cleared on this platform; the source system has not confirmed it yet. '
              + 'Its status was recorded when the order was placed, moments before the payment landed.'
            : '';
    }

    /** Paid here, still unpaid there. The disagreement this label exists to explain. */
    private awaitingConfirmation(o: LocationOrderListItem): boolean {
        return !!o.payment?.isCaptured
            && this.AWAITING_PAYMENT.includes((o.status || '').toLowerCase());
    }

    /** What the LEDGER says, in one word. Not derived from the order's own status — that is the point. */
    paymentLabel(pay: OrderPaymentSummary): string {
        if (pay.isCaptured) return pay.matches ? 'paid' : 'paid · amount differs';
        return (pay.status || 'pending').toLowerCase();
    }

    /** Green only when money moved AND it matches the order. An authorization is not a payment, and a
     *  captured amount that differs from the total is a discrepancy, not a success. */
    paymentSev(pay: OrderPaymentSummary): 'success' | 'warn' | 'danger' | 'info' {
        if (pay.isCaptured) return pay.matches ? 'success' : 'danger';
        const status = (pay.status || '').toLowerCase();
        if (status.includes('fail') || status.includes('declin')) return 'danger';
        return 'warn';
    }

    statusSev(s: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const v = (s ?? '').toLowerCase();
        if (!v) return 'secondary';
        if (/(complete|captured|paid|delivered|fulfilled|shipped|closed|success|approved)/.test(v)) return 'success';
        if (/(cancel|fail|declin|void|error|reject|denied)/.test(v)) return 'danger';
        if (/(refund|hold|partial|dispute|chargeback)/.test(v)) return 'warn';
        if (/(pending|processing|new|open|created|authoriz|received|placed)/.test(v)) return 'info';
        return 'secondary';
    }

    // Is this dispatch status one of the deliberate waits (HeldForPayment)? Substring-matched like every other
    // status classifier here, so the server can name the state without the client needing a new release.
    dispatchHeld(s: string | null | undefined): boolean {
        return /(held|hold)/.test((s ?? '').toLowerCase());
    }

    dispatchSev(s: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const v = (s ?? '').toLowerCase();
        // 'sync-failed' must hit the danger arm, so failure tokens are tested FIRST — 'synced'
        // ('sent' substring...) and the other success tokens would otherwise swallow it.
        if (/(fail|error|rejected)/.test(v)) return 'danger';
        // 'refunded' and 'issued' are the outcomes of a refund made at the register and of its credit memo;
        // 'refund-failed' has already left through the arm above.
        if (/(dispatched|sent|notified|complete|success|acknowledged|synced|delivered|advanced|refunded|issued)/.test(v)) return 'success';
        // 'held' as well as 'hold': HeldForPayment is a wait an operator should see as a wait, not as
        // an unknown grey status.
        if (/(retry|held|hold|skipped)/.test(v)) return 'warn';
        if (/(pending|processing|queued)/.test(v)) return 'info';
        return 'secondary';
    }

    deliverySev(s: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const v = (s ?? '').toLowerCase();
        if (/(delivered|dropped_off|dropped off|complete)/.test(v)) return 'success';
        if (/(cancel|fail|return)/.test(v)) return 'danger';
        if (/(delay|hold)/.test(v)) return 'warn';
        return 'info';
    }

    sourceSev(s: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const v = (s ?? '').toLowerCase();
        if (v === 'magento') return 'info';
        if (v === 'doordash') return 'warn';
        return 'secondary';
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.status === 401) return 'Unauthorized — signing in…';
        if (e?.status === 403) return 'Forbidden — your role lacks the orders:read permission.';
        if (e?.status === 400) {
            const server = e?.error?.detail ?? e?.error?.message ?? e?.error?.title;
            return server ?? 'Bad request — this order id may exist under more than one source; a source is required.';
        }
        const server = e?.error?.detail ?? e?.error?.message;
        return server ?? `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
